/* Component to continue to add and also edit the Company and Crew Info 
 * Contains the CrewCompany, CrewProduction, CrewCastingAD,
   CrewLocations, CrewScriptCater, CrewElecGrip, CrewMakeup,
   CrewSoundTransport, CrewStunts, CrewArt, CrewCamera,
   CrewPostAdditional and CrewWardrobe Form components which are
   held in the addCrewInfoByDept folder to add info by department */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import styles from "../../../styles/Callsheets.module.css";
import btnStyles from "../../../styles/Button.module.css";
import { useHistory, useParams } from "react-router-dom";
import { axiosInstance, axiosReq } from "../../../api/axiosDefaults";
import TopBox from "../../../components/TopBox";
import { useRedirect } from "../../../hooks/Redirect";
import {
  useCrewInfoContext,
  useSetEditCrewInfoContext,
} from "../../../contexts/BaseCallContext";

import CrewCompany from "./addCrewInfoByDept/CrewCompany";
import CrewProduction from "./addCrewInfoByDept/CrewProduction";
import CrewLocations from "./addCrewInfoByDept/CrewLocations";
import CrewElecGrip from "./addCrewInfoByDept/CrewElecGrip";
import CrewMakeup from "./addCrewInfoByDept/CrewMakeup";
import CrewStunts from "./addCrewInfoByDept/CrewStunts";
import CrewArt from "./addCrewInfoByDept/CrewArt";
import CrewCamera from "./addCrewInfoByDept/CrewCamera";
import CrewWardrobe from "./addCrewInfoByDept/CrewWardrobe";
import CrewScript from "./addCrewInfoByDept/CrewScript";
import CrewCater from "./addCrewInfoByDept/CrewCater";
import CrewCasting from "./addCrewInfoByDept/CrewCasting";
import CrewADPA from "./addCrewInfoByDept/CrewADPA";
import CrewTransport from "./addCrewInfoByDept/CrewTransport";
import CrewPostVFX from "./addCrewInfoByDept/CrewPostVFX";
import CrewWrangling from "./addCrewInfoByDept/CrewWrangling";
import CrewTVStudio from "./addCrewInfoByDept/CrewTVStudio";
import CrewAdditional from "./addCrewInfoByDept/CrewAdditional";
import CrewSound from "./addCrewInfoByDept/CrewSound";
import { CLIENT_PROGRAM_HOSTNAME } from "../../../utils/config";
import useHostName from "../../../hooks/useHostName";
import toast from "react-hot-toast";
import ImpoEditpage from "../info/ImpoEditpage";

// Shared crew role prefixes used to drive autofill, read-only handling,
// and edit-mode dropdown preselection across all crew departments.
const CREW_ROLE_PREFIXES = [
  "director",
  "producer",
  "pro_coordinator",
  "upm",
  "travel_coordinator",
  "production_pa",
  "locations_manager",
  "set_medic",
  "oth_production_pos_1",
  "oth_production_pos_2",
  "oth_production_pos_3",
  "oth_production_pos_4",
  "oth_production_pos_5",
  "ad_1",
  "ad_2",
  "ad_3",
  "ad_4",
  "ad_5",
  "pro_assistant_1",
  "pro_assistant_2",
  "pro_assistant_3",
  "pro_assistant_4",
  "pro_assistant_5",
  "dop",
  "camera_operator",
  "camera_ass_1",
  "camera_ass_2",
  "dit",
  "steadicam",
  "camera_pa",
  "oth_camera_pos_1",
  "oth_camera_pos_2",
  "oth_camera_pos_3",
  "oth_camera_pos_4",
  "oth_camera_pos_5",
  "xtra_camera_pos_1",
  "xtra_camera_pos_2",
  "xtra_camera_pos_3",
  "xtra_camera_pos_4",
  "xtra_camera_pos_5",
  "xtra_camera_pos_6",
  "xtra_camera_pos_7",
  "xtra_camera_pos_8",
  "xtra_camera_pos_9",
  "xtra_camera_pos_10",
  "key_hairmakeup",
  "key_hairstylist",
  "sfx_makeup",
  "sfx_makeup_assistant",
  "makeup_artist_1",
  "makeup_artist_2",
  "makeup_artist_3",
  "makeup_artist_4",
  "makeup_artist_5",
  "sound_mixer",
  "boom_operator",
  "sound_assistant_1",
  "sound_assistant_2",
  "script_supervisor",
  "costume_designer",
  "ass_costume_designer",
  "casting_director",
  "wardrobe_assistant_1",
  "wardrobe_assistant_2",
  "wardrobe_assistant_3",
  "wardrobe_assistant_4",
  "wardrobe_assistant_5",
  "production_designer",
  "art_director",
  "art_assistant",
  "set_decorator",
  "set_dresser",
  "lead_man",
  "dresser",
  "prop_master",
  "ass_prop_master",
  "prop_buyer",
  "armorer",
  "gaffer",
  "best_boy_electric",
  "electric_3",
  "electric_4",
  "keygrip",
  "best_boy_grip",
  "dolly_grip",
  "swing_ge1",
  "swing_ge2",
  "swing_ge3",
  "swing_ge4",
  "swing_ge5",
  "stunt_coordinator",
  "stunts_1",
  "stunts_2",
  "stunts_3",
  "stunts_4",
  "stunts_5",
  "legal",
  "editor",
  "writer",
  "fx",
  "extras_casting",
  "location_mngr",
  "location_ass_1",
  "location_ass_2",
  "location_ass_3",
  "location_ass_4",
  "location_security",
  "transport_captain",
  "transport_manager_1",
  "transport_manager_2",
  "head_driver",
  "truck1",
  "truck2",
  "truck3",
  "car1",
  "car2",
  "car3",
  "catering_co_1",
  "catering_co_2",
  "catering_co_3",
  "craft_service",
  "crafty_ass",
  "add_pos_1",
  "add_pos_2",
  "add_pos_3",
  "add_pos_4",
  "add_pos_5",
  "add_pos_6",
  "add_pos_7",
  "add_pos_8",
  "add_pos_9",
  "add_pos_10",
  "add_pos_11",
  "add_pos_12",
  "add_pos_13",
  "add_pos_14",
  "add_pos_15",
  "add_pos_16",
  "add_pos_17",
  "add_pos_18",
  "add_pos_19",
  "add_pos_20",
];

const CREW_READ_ONLY_FIELD_NAMES = new Set(
  CREW_ROLE_PREFIXES.flatMap((rolePrefix) => [
    `${rolePrefix}_name`,
    `${rolePrefix}_phone`,
    `${rolePrefix}_email`,
  ])
);

const CrewInfoEdit = () => {
  const host = useHostName();
  useRedirect();
  // eslint-disable-next-line
  const [errors, setErrors] = useState({});
  const [crewOptions, setCrewOptions] = useState([]);
  const [crewOptionsAll, setCrewOptionsAll] = useState([]);
  const [crewSearchQuery, setCrewSearchQuery] = useState("");
  const [crewSearchLoading, setCrewSearchLoading] = useState(false);
  const [selectedCrewIds, setSelectedCrewIds] = useState({});
  const history = useHistory();
  const { id } = useParams();
  const setEditCrewInfo = useSetEditCrewInfoContext();
  const crewInfoData = useCrewInfoContext();
  console.log("crewInfoData", crewInfoData);
  // eslint-disable-next-line
  const [show, setShow] = useState(false);
  const [showImp, setShowImp] = useState(false);

  const [showCom, setShowCom] = useState(false);
  const [showPro, setShowPro] = useState(false);
  const [showCam, setShowCam] = useState(false);
  const [showSou, setShowSou] = useState(false);
  const [showLoc, setShowLoc] = useState(false);
  const [showMak, setShowMak] = useState(false);
  const [showEle, setShowEle] = useState(false);
  const [showScr, setShowScr] = useState(false);
  const [showArt, setShowArt] = useState(false);
  const [showCas, setShowCas] = useState(false);
  const [showStu, setShowStu] = useState(false);
  const [showPos, setShowPos] = useState(false);
  const [showWar, setShowWar] = useState(false);
  const [showCat, setShowCat] = useState(false);
  const [showAD, setShowAD] = useState(false); 
  const [showTra, setShowTra] = useState(false); 
  const [showAdd, setShowAdd] = useState(false);
  const [showTv, setShowTv] = useState(false);
  const [showWra, setShowWra] = useState(false);

  const getUsersPayloadArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const buildCrewUsersUrl = (searchValue) => {
    const trimmed = String(searchValue || "").trim();
    const base = "/users/?except_group_name=Cast";
    if (!trimmed) return base;
    return `${base}&search=${encodeURIComponent(trimmed)}`;
  };

  const getFullName = (user) => {
    const firstName = user?.first_name?.trim() || "";
    const lastName = user?.last_name?.trim() || "";
    return `${firstName} ${lastName}`.trim();
  };

  const normalizeValue = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const normalizePhone = (value) => String(value || "").replace(/\D/g, "");

  /**
   * Normalizes a single crew FK from GET `crewinfonew` JSON into a string user id for Mantine selects.
   * DRF may return `director_user` (etc.) as a bare integer, a string id, or a nested user object `{ id }`.
   * Empty / null means no linked user.
   */
  const pickUserIdFromApi = (raw) => {
    if (raw == null || raw === "") return "";
    if (typeof raw === "object" && raw !== null && raw.id != null) {
      return String(raw.id);
    }
    return String(raw);
  };

  /**
   * After loading crew info from the API, fills `selectedCrewIds` from each `*_user` field on the response
   * (e.g. `director_user`, `add_pos_1_user`) so department crew dropdowns show the saved selection.
   * Only keys present on `apiData` are applied; missing keys leave prior state unchanged for that role.
   * Requires the backend to include these FK fields on GET; if omitted, dropdowns rely on name/email/phone inference only.
   */
  const applyCrewUserIdsFromApi = (apiData) => {
    setSelectedCrewIds((prev) => {
      const next = { ...prev };
      CREW_ROLE_PREFIXES.forEach((prefix) => {
        const key = `${prefix}_user`;
        if (!Object.prototype.hasOwnProperty.call(apiData, key)) return;
        const id = pickUserIdFromApi(apiData[key]);
        if (id) next[prefix] = id;
        else delete next[prefix];
      });
      return next;
    });
  };

  const showErrorToast = (err, fallback) => {
    let backendError = fallback;
    if (err?.response?.data?.error) {
      backendError = err.response.data.error;
    } else if (err?.response?.data?.detail) {
      backendError = err.response.data.detail;
    } else if (err?.message) {
      backendError = err.message;
    }
    toast.error(backendError);
  };

  const [postData, setPostData] = useState({
    // company
    total_shoot_days: "",
    production_name: "",
    production_company: "",
    company_email: "",
    company_phone: "",
    company_address_line_1: "",
    company_address_line_2: "",
    company_address_line_3: "",
    company_address_line_4: "",
    // company_logo: "",
    // production
    director_name: "",
    director_email: "",
    director_phone: "",
    producer_name: "",
    producer_email: "",
    producer_phone: "",
    pro_coordinator_name: "",
    pro_coordinator_email: "",
    pro_coordinator_phone: "",
    upm_name: "",
    upm_email: "",
    upm_phone: "",
    travel_coordinator_name: "",
    travel_coordinator_email: "",
    travel_coordinator_phone: "",
    production_pa_name: "",
    production_pa_email: "",
    production_pa_phone: "",
    oth_production_pos_1_job: "",
    oth_production_pos_1_name: "",
    oth_production_pos_1_email: "",
    oth_production_pos_1_phone: "",
    oth_production_pos_2_job: "",
    oth_production_pos_2_name: "",
    oth_production_pos_2_email: "",
    oth_production_pos_2_phone: "",
    oth_production_pos_3_job: "",
    oth_production_pos_3_name: "",
    oth_production_pos_3_email: "",
    oth_production_pos_3_phone: "",
    oth_production_pos_4_job: "",
    oth_production_pos_4_name: "",
    oth_production_pos_4_email: "",
    oth_production_pos_4_phone: "",
    oth_production_pos_5_job: "",
    oth_production_pos_5_name: "",
    oth_production_pos_5_email: "",
    oth_production_pos_5_phone: "",
    // continuity
    script_supervisor_name: "",
    script_supervisor_email: "",
    script_supervisor_phone: "",
    // legal/medic
    legal_name: "",
    legal_email: "",
    legal_phone: "",
    set_medic_name: "",
    set_medic_email: "",
    set_medic_phone: "",
    // ad
    ad_1_name: "",
    ad_1_email: "",
    ad_1_phone: "",
    ad_2_name: "",
    ad_2_email: "",
    ad_2_phone: "",
    ad_3_name: "",
    ad_3_email: "",
    ad_3_phone: "",
    ad_4_name: "",
    ad_4_email: "",
    ad_4_phone: "",
    ad_5_name: "",
    ad_5_email: "",
    ad_5_phone: "",
    pro_assistant_1_name: "",
    pro_assistant_1_email: "",
    pro_assistant_1_phone: "",
    pro_assistant_2_name: "",
    pro_assistant_2_email: "",
    pro_assistant_2_phone: "",
    pro_assistant_3_name: "",
    pro_assistant_3_email: "",
    pro_assistant_3_phone: "",
    pro_assistant_4_name: "",
    pro_assistant_4_email: "",
    pro_assistant_4_phone: "",
    pro_assistant_5_name: "",
    pro_assistant_5_email: "",
    pro_assistant_5_phone: "",
    // camera
    dop_name: "",
    dop_email: "",
    dop_phone: "",
    camera_operator_name: "",
    camera_operator_email: "",
    camera_operator_phone: "",
    camera_ass_1_name: "",
    camera_ass_1_email: "",
    camera_ass_1_phone: "",
    camera_ass_2_name: "",
    camera_ass_2_email: "",
    camera_ass_2_phone: "",
    dit_name: "",
    dit_email: "",
    dit_phone: "",
    steadicam_name: "",
    steadicam_email: "",
    steadicam_phone: "",
    camera_pa_name: "",
    camera_pa_email: "",
    camera_pa_phone: "",
    oth_camera_pos_1_job: "",
    oth_camera_pos_1_name: "",
    oth_camera_pos_1_email: "",
    oth_camera_pos_1_phone: "",
    oth_camera_pos_2_job: "",
    oth_camera_pos_2_name: "",
    oth_camera_pos_2_email: "",
    oth_camera_pos_2_phone: "",
    oth_camera_pos_3_job: "",
    oth_camera_pos_3_name: "",
    oth_camera_pos_3_email: "",
    oth_camera_pos_3_phone: "",
    oth_camera_pos_4_job: "",
    oth_camera_pos_4_name: "",
    oth_camera_pos_4_email: "",
    oth_camera_pos_4_phone: "",
    oth_camera_pos_5_job: "",
    oth_camera_pos_5_name: "",
    oth_camera_pos_5_email: "",
    oth_camera_pos_5_phone: "",
    // sound
    sound_mixer_name: "",
    sound_mixer_email: "",
    sound_mixer_phone: "",
    boom_operator_name: "",
    boom_operator_email: "",
    boom_operator_phone: "",
    sound_assistant_1_name: "",
    sound_assistant_1_email: "",
    sound_assistant_1_phone: "",
    sound_assistant_2_name: "",
    sound_assistant_2_email: "",
    sound_assistant_2_phone: "",
    // hair/makeup
    key_hairmakeup_name: "",
    key_hairmakeup_email: "",
    key_hairmakeup_phone: "",
    key_hairstylist_name: "",
    key_hairstylist_email: "",
    key_hairstylist_phone: "",
    sfx_makeup_name: "",
    sfx_makeup_email: "",
    sfx_makeup_phone: "",
    sfx_makeup_assistant_name: "",
    sfx_makeup_assistant_email: "",
    sfx_makeup_assistant_phone: "",
    makeup_artist_1_name: "",
    makeup_artist_1_email: "",
    makeup_artist_1_phone: "",
    makeup_artist_2_name: "",
    makeup_artist_2_email: "",
    makeup_artist_2_phone: "",
    makeup_artist_3_name: "",
    makeup_artist_3_email: "",
    makeup_artist_3_phone: "",
    makeup_artist_4_name: "",
    makeup_artist_4_email: "",
    makeup_artist_4_phone: "",
    makeup_artist_5_name: "",
    makeup_artist_5_email: "",
    makeup_artist_5_phone: "",
    // wardrobe
    costume_designer_name: "",
    costume_designer_email: "",
    costume_designer_phone: "",
    ass_costume_designer_name: "",
    ass_costume_designer_email: "",
    ass_costume_designer_phone: "",
    wardrobe_assistant_1_name: "",
    wardrobe_assistant_1_email: "",
    wardrobe_assistant_1_phone: "",
    wardrobe_assistant_2_name: "",
    wardrobe_assistant_2_email: "",
    wardrobe_assistant_2_phone: "",
    wardrobe_assistant_3_name: "",
    wardrobe_assistant_3_email: "",
    wardrobe_assistant_3_phone: "",
    wardrobe_assistant_4_name: "",
    wardrobe_assistant_4_email: "",
    wardrobe_assistant_4_phone: "",
    wardrobe_assistant_5_name: "",
    wardrobe_assistant_5_email: "",
    wardrobe_assistant_5_phone: "",
    // art
    production_designer_name: "",
    production_designer_email: "",
    production_designer_phone: "",
    art_director_name: "",
    art_director_email: "",
    art_director_phone: "",
    art_assistant_name: "",
    art_assistant_email: "",
    art_assistant_phone: "",
    set_decorator_name: "",
    set_decorator_email: "",
    set_decorator_phone: "",
    set_dresser_name: "",
    set_dresser_email: "",
    set_dresser_phone: "",
    lead_man_name: "",
    lead_man_email: "",
    lead_man_phone: "",
    dresser_name: "",
    dresser_email: "",
    dresser_phone: "",
    prop_master_name: "",
    prop_master_email: "",
    prop_master_phone: "",
    ass_prop_master_name: "",
    ass_prop_master_email: "",
    ass_prop_master_phone: "",
    prop_buyer_name: "",
    prop_buyer_email: "",
    prop_buyer_phone: "",
    armorer_name: "",
    armorer_email: "",
    armorer_phone: "",
    // electric
    gaffer_name: "",
    gaffer_email: "",
    gaffer_phone: "",
    best_boy_electric_name: "",
    best_boy_electric_email: "",
    best_boy_electric_phone: "",
    electric_3_name: "",
    electric_3_email: "",
    electric_3_phone: "",
    electric_4_name: "",
    electric_4_email: "",
    electric_4_phone: "",
    // grip
    keygrip_name: "",
    keygrip_email: "",
    keygrip_phone: "",
    best_boy_grip_name: "",
    best_boy_grip_email: "",
    best_boy_grip_phone: "",
    dolly_grip_name: "",
    dolly_grip_email: "",
    dolly_grip_phone: "",
    swing_ge1_name: "",
    swing_ge1_email: "",
    swing_ge1_phone: "",
    swing_ge2_name: "",
    swing_ge2_email: "",
    swing_ge2_phone: "",
    swing_ge3_name: "",
    swing_ge3_email: "",
    swing_ge3_phone: "",
    swing_ge4_name: "",
    swing_ge4_email: "",
    swing_ge4_phone: "",
    swing_ge5_name: "",
    swing_ge5_email: "",
    swing_ge5_phone: "",
    // stunts
    stunt_coordinator_name: "",
    stunt_coordinator_email: "",
    stunt_coordinator_phone: "",
    stunts_1_name: "",
    stunts_1_email: "",
    stunts_1_phone: "",
    stunts_2_name: "",
    stunts_2_email: "",
    stunts_2_phone: "",
    stunts_3_name: "",
    stunts_3_email: "",
    stunts_3_phone: "",
    stunts_4_name: "",
    stunts_4_email: "",
    stunts_4_phone: "",
    stunts_5_name: "",
    stunts_5_email: "",
    stunts_5_phone: "",
    // post
    editor_name: "",
    editor_email: "",
    editor_phone: "",
    fx_name: "",
    fx_email: "",
    fx_phone: "",
    // script
    writer_name: "",
    writer_email: "",
    writer_phone: "",
    // casting
    casting_director_name: "",
    casting_director_email: "",
    casting_director_phone: "",
    extras_casting_name: "",
    extras_casting_email: "",
    extras_casting_phone: "",
    // locations
    location_mngr_name: "",
    location_mngr_email: "",
    location_mngr_phone: "",
    location_ass_1_name: "",
    location_ass_1_email: "",
    location_ass_1_phone: "",
    location_ass_2_name: "",
    location_ass_2_email: "",
    location_ass_2_phone: "",
    location_ass_3_name: "",
    location_ass_3_email: "",
    location_ass_3_phone: "",
    location_ass_4_name: "",
    location_ass_4_email: "",
    location_ass_4_phone: "",
    location_security_name: "",
    location_security_email: "",
    location_security_phone: "",
    // transport
    transport_captain_name: "",
    transport_captain_email: "",
    transport_captain_phone: "",
    transport_manager_1_name: "",
    transport_manager_1_email: "",
    transport_manager_1_phone: "",
    transport_manager_2_name: "",
    transport_manager_2_email: "",
    transport_manager_2_phone: "",
    head_driver_name: "",
    head_driver_email: "",
    head_driver_phone: "",
    car1_name: "",
    car1_email: "",
    car1_phone: "",
    car2_name: "",
    car2_email: "",
    car2_phone: "",
    car3_name: "",
    car3_email: "",
    car3_phone: "",
    truck1_name: "",
    truck1_email: "",
    truck1_phone: "",
    truck2_name: "",
    truck2_email: "",
    truck2_phone: "",
    truck3_name: "",
    truck3_email: "",
    truck3_phone: "",
    // catering
    craft_service_name: "",
    craft_service_email: "",
    craft_service_phone: "",
    crafty_ass_name: "",
    crafty_ass_email: "",
    crafty_ass_phone: "",
    catering_co_1_name: "",
    catering_co_1_email: "",
    catering_co_1_phone: "",
    catering_co_2_name: "",
    catering_co_2_email: "",
    catering_co_2_phone: "",
    catering_co_3_name: "",
    catering_co_3_email: "",
    catering_co_3_phone: "",
    // additional
    add_pos_1_job: "",
    add_pos_1_name: "",
    add_pos_1_email: "",
    add_pos_1_phone: "",
    add_pos_2_job: "",
    add_pos_2_name: "",
    add_pos_2_email: "",
    add_pos_2_phone: "",
    add_pos_3_job: "",
    add_pos_3_name: "",
    add_pos_3_email: "",
    add_pos_3_phone: "",
    add_pos_4_job: "",
    add_pos_4_name: "",
    add_pos_4_email: "",
    add_pos_4_phone: "",
    add_pos_5_job: "",
    add_pos_5_name: "",
    add_pos_5_email: "",
    add_pos_5_phone: "",
    add_pos_6_job: "",
    add_pos_6_name: "",
    add_pos_6_email: "",
    add_pos_6_phone: "",
    add_pos_7_job: "",
    add_pos_7_name: "",
    add_pos_7_email: "",
    add_pos_7_phone: "",
    add_pos_8_job: "",
    add_pos_8_name: "",
    add_pos_8_email: "",
    add_pos_8_phone: "",
    add_pos_9_job: "",
    add_pos_9_name: "",
    add_pos_9_email: "",
    add_pos_9_phone: "",
    add_pos_10_job: "",
    add_pos_10_name: "",
    add_pos_10_email: "",
    add_pos_10_phone: "",
    // add pos 11-20
    add_pos_11_job: "",
    add_pos_11_name: "",
    add_pos_11_email: "",
    add_pos_11_phone: "",
    add_pos_12_job: "",
    add_pos_12_name: "",
    add_pos_12_email: "",
    add_pos_12_phone: "",
    add_pos_13_job: "",
    add_pos_13_name: "",
    add_pos_13_email: "",
    add_pos_13_phone: "",
    add_pos_14_job: "",
    add_pos_14_name: "",
    add_pos_14_email: "",
    add_pos_14_phone: "",
    add_pos_15_job: "",
    add_pos_15_name: "",
    add_pos_15_email: "",
    add_pos_15_phone: "",
    add_pos_16_job: "",
    add_pos_16_name: "",
    add_pos_16_email: "",
    add_pos_16_phone: "",
    add_pos_17_job: "",
    add_pos_17_name: "",
    add_pos_17_email: "",
    add_pos_17_phone: "",
    add_pos_18_job: "",
    add_pos_18_name: "",
    add_pos_18_email: "",
    add_pos_18_phone: "",
    add_pos_19_job: "",
    add_pos_19_name: "",
    add_pos_19_email: "",
    add_pos_19_phone: "",
    add_pos_20_job: "",
    add_pos_20_name: "",
    add_pos_20_email: "",
    add_pos_20_phone: "",
  });

  const {
    // company
    production_name,
    production_company,
    company_phone,
    company_email,
    company_address_line_1,
    company_address_line_2,
    company_address_line_3,
    company_address_line_4,
    total_shoot_days,
    // company_logo,
    // production
    director_name,
    director_email,
    director_phone,
    producer_name,
    producer_email,
    producer_phone,
    pro_coordinator_name,
    pro_coordinator_email,
    pro_coordinator_phone,
    upm_name,
    upm_email,
    upm_phone,
    travel_coordinator_name,
    travel_coordinator_email,
    travel_coordinator_phone,
    production_pa_name,
    production_pa_email,
    production_pa_phone,
    oth_production_pos_1_job,
    oth_production_pos_1_name,
    oth_production_pos_1_email,
    oth_production_pos_1_phone,
    oth_production_pos_2_job,
    oth_production_pos_2_name,
    oth_production_pos_2_email,
    oth_production_pos_2_phone,
    oth_production_pos_3_job,
    oth_production_pos_3_name,
    oth_production_pos_3_email,
    oth_production_pos_3_phone,
    oth_production_pos_4_job,
    oth_production_pos_4_name,
    oth_production_pos_4_email,
    oth_production_pos_4_phone,
    oth_production_pos_5_job,
    oth_production_pos_5_name,
    oth_production_pos_5_email,
    oth_production_pos_5_phone,
    script_supervisor_name,
    script_supervisor_email,
    script_supervisor_phone,
    legal_name,
    legal_email,
    legal_phone,
    set_medic_name,
    set_medic_email,
    set_medic_phone,
    // casting
    casting_director_name,
    casting_director_email,
    casting_director_phone,
    extras_casting_name,
    extras_casting_email,
    extras_casting_phone,
    // AD
    ad_1_name,
    ad_1_email,
    ad_1_phone,
    ad_2_name,
    ad_2_email,
    ad_2_phone,
    ad_3_name,
    ad_3_email,
    ad_3_phone,
    ad_4_name,
    ad_4_email,
    ad_4_phone,
    ad_5_name,
    ad_5_email,
    ad_5_phone,
    pro_assistant_1_name,
    pro_assistant_1_email,
    pro_assistant_1_phone,
    pro_assistant_2_name,
    pro_assistant_2_email,
    pro_assistant_2_phone,
    pro_assistant_3_name,
    pro_assistant_3_email,
    pro_assistant_3_phone,
    pro_assistant_4_name,
    pro_assistant_4_email,
    pro_assistant_4_phone,
    pro_assistant_5_name,
    pro_assistant_5_email,
    pro_assistant_5_phone,
    // camera
    dop_name,
    dop_email,
    dop_phone,
    camera_operator_name,
    camera_operator_email,
    camera_operator_phone,
    camera_ass_1_name,
    camera_ass_1_email,
    camera_ass_1_phone,
    camera_ass_2_name,
    camera_ass_2_email,
    camera_ass_2_phone,
    dit_name,
    dit_email,
    dit_phone,
    steadicam_name,
    steadicam_email,
    steadicam_phone,
    camera_pa_name,
    camera_pa_email,
    camera_pa_phone,
    oth_camera_pos_1_job,
    oth_camera_pos_1_name,
    oth_camera_pos_1_email,
    oth_camera_pos_1_phone,
    oth_camera_pos_2_job,
    oth_camera_pos_2_name,
    oth_camera_pos_2_email,
    oth_camera_pos_2_phone,
    oth_camera_pos_3_job,
    oth_camera_pos_3_name,
    oth_camera_pos_3_email,
    oth_camera_pos_3_phone,
    oth_camera_pos_4_job,
    oth_camera_pos_4_name,
    oth_camera_pos_4_email,
    oth_camera_pos_4_phone,
    oth_camera_pos_5_job,
    oth_camera_pos_5_name,
    oth_camera_pos_5_email,
    oth_camera_pos_5_phone,
    // sound
    sound_mixer_name,
    sound_mixer_email,
    sound_mixer_phone,
    boom_operator_name,
    boom_operator_email,
    boom_operator_phone,
    sound_assistant_1_name,
    sound_assistant_1_email,
    sound_assistant_1_phone,
    sound_assistant_2_name,
    sound_assistant_2_email,
    sound_assistant_2_phone,
    // transport
    transport_captain_name,
    transport_captain_email,
    transport_captain_phone,
    transport_manager_1_name,
    transport_manager_1_email,
    transport_manager_1_phone,
    transport_manager_2_name,
    transport_manager_2_email,
    transport_manager_2_phone,
    head_driver_name,
    head_driver_email,
    head_driver_phone,
    car1_name,
    car1_email,
    car1_phone,
    car2_name,
    car2_email,
    car2_phone,
    car3_name,
    car3_email,
    car3_phone,
    truck1_name,
    truck1_email,
    truck1_phone,
    truck2_name,
    truck2_email,
    truck2_phone,
    truck3_name,
    truck3_email,
    truck3_phone,
    // makeup
    key_hairmakeup_name,
    key_hairmakeup_email,
    key_hairmakeup_phone,
    key_hairstylist_name,
    key_hairstylist_email,
    key_hairstylist_phone,
    sfx_makeup_name,
    sfx_makeup_email,
    sfx_makeup_phone,
    sfx_makeup_assistant_name,
    sfx_makeup_assistant_email,
    sfx_makeup_assistant_phone,
    makeup_artist_1_name,
    makeup_artist_1_email,
    makeup_artist_1_phone,
    makeup_artist_2_name,
    makeup_artist_2_email,
    makeup_artist_2_phone,
    makeup_artist_3_name,
    makeup_artist_3_email,
    makeup_artist_3_phone,
    makeup_artist_4_name,
    makeup_artist_4_email,
    makeup_artist_4_phone,
    makeup_artist_5_name,
    makeup_artist_5_email,
    makeup_artist_5_phone,
    // wardrobe
    costume_designer_name,
    costume_designer_email,
    costume_designer_phone,
    ass_costume_designer_name,
    ass_costume_designer_email,
    ass_costume_designer_phone,
    wardrobe_assistant_1_name,
    wardrobe_assistant_1_email,
    wardrobe_assistant_1_phone,
    wardrobe_assistant_2_name,
    wardrobe_assistant_2_email,
    wardrobe_assistant_2_phone,
    wardrobe_assistant_3_name,
    wardrobe_assistant_3_email,
    wardrobe_assistant_3_phone,
    wardrobe_assistant_4_name,
    wardrobe_assistant_4_email,
    wardrobe_assistant_4_phone,
    wardrobe_assistant_5_name,
    wardrobe_assistant_5_email,
    wardrobe_assistant_5_phone,
    // art
    production_designer_name,
    production_designer_email,
    production_designer_phone,
    art_director_name,
    art_director_email,
    art_director_phone,
    art_assistant_name,
    art_assistant_email,
    art_assistant_phone,
    set_decorator_name,
    set_decorator_email,
    set_decorator_phone,
    set_dresser_name,
    set_dresser_email,
    set_dresser_phone,
    lead_man_name,
    lead_man_email,
    lead_man_phone,
    dresser_name,
    dresser_email,
    dresser_phone,
    prop_master_name,
    prop_master_email,
    prop_master_phone,
    ass_prop_master_name,
    ass_prop_master_email,
    ass_prop_master_phone,
    prop_buyer_name,
    prop_buyer_email,
    prop_buyer_phone,
    armorer_name,
    armorer_email,
    armorer_phone,
    // electric/grip
    gaffer_name,
    gaffer_email,
    gaffer_phone,
    best_boy_electric_name,
    best_boy_electric_email,
    best_boy_electric_phone,
    electric_3_name,
    electric_3_email,
    electric_3_phone,
    electric_4_name,
    electric_4_email,
    electric_4_phone,
    keygrip_name,
    keygrip_email,
    keygrip_phone,
    best_boy_grip_name,
    best_boy_grip_email,
    best_boy_grip_phone,
    dolly_grip_name,
    dolly_grip_email,
    dolly_grip_phone,
    swing_ge1_name,
    swing_ge1_email,
    swing_ge1_phone,
    swing_ge2_name,
    swing_ge2_email,
    swing_ge2_phone,
    swing_ge3_name,
    swing_ge3_email,
    swing_ge3_phone,
    swing_ge4_name,
    swing_ge4_email,
    swing_ge4_phone,
    swing_ge5_name,
    swing_ge5_email,
    swing_ge5_phone,
    // stunts
    stunt_coordinator_name,
    stunt_coordinator_email,
    stunt_coordinator_phone,
    stunts_1_name,
    stunts_1_email,
    stunts_1_phone,
    stunts_2_name,
    stunts_2_email,
    stunts_2_phone,
    stunts_3_name,
    stunts_3_email,
    stunts_3_phone,
    stunts_4_name,
    stunts_4_email,
    stunts_4_phone,
    stunts_5_name,
    stunts_5_email,
    stunts_5_phone,
    // post/script
    editor_name,
    editor_email,
    editor_phone,
    fx_name,
    fx_email,
    fx_phone,
    writer_name,
    writer_email,
    writer_phone,
    // catering
    catering_co_1_name,
    catering_co_1_email,
    catering_co_1_phone,
    catering_co_2_name,
    catering_co_2_email,
    catering_co_2_phone,
    catering_co_3_name,
    catering_co_3_email,
    catering_co_3_phone,
    craft_service_name,
    craft_service_email,
    craft_service_phone,
    crafty_ass_name,
    crafty_ass_email,
    crafty_ass_phone,
    // locations
    location_mngr_name,
    location_mngr_email,
    location_mngr_phone,
    location_ass_1_name,
    location_ass_1_email,
    location_ass_1_phone,
    location_ass_2_name,
    location_ass_2_email,
    location_ass_2_phone,
    location_ass_3_name,
    location_ass_3_email,
    location_ass_3_phone,
    location_ass_4_name,
    location_ass_4_email,
    location_ass_4_phone,
    location_security_name,
    location_security_email,
    location_security_phone,
    // additional 1-10
    add_pos_1_job,
    add_pos_1_name,
    add_pos_1_email,
    add_pos_1_phone,
    add_pos_2_job,
    add_pos_2_name,
    add_pos_2_email,
    add_pos_2_phone,
    add_pos_3_job,
    add_pos_3_name,
    add_pos_3_email,
    add_pos_3_phone,
    add_pos_4_job,
    add_pos_4_name,
    add_pos_4_email,
    add_pos_4_phone,
    add_pos_5_job,
    add_pos_5_name,
    add_pos_5_email,
    add_pos_5_phone,
    add_pos_6_job,
    add_pos_6_name,
    add_pos_6_email,
    add_pos_6_phone,
    add_pos_7_job,
    add_pos_7_name,
    add_pos_7_email,
    add_pos_7_phone,
    add_pos_8_job,
    add_pos_8_name,
    add_pos_8_email,
    add_pos_8_phone,
    add_pos_9_job,
    add_pos_9_name,
    add_pos_9_email,
    add_pos_9_phone,
    add_pos_10_job,
    add_pos_10_name,
    add_pos_10_email,
    add_pos_10_phone,
    // additional 11-20
    add_pos_11_job,
    add_pos_11_name,
    add_pos_11_email,
    add_pos_11_phone,
    add_pos_12_job,
    add_pos_12_name,
    add_pos_12_email,
    add_pos_12_phone,
    add_pos_13_job,
    add_pos_13_name,
    add_pos_13_email,
    add_pos_13_phone,
    add_pos_14_job,
    add_pos_14_name,
    add_pos_14_email,
    add_pos_14_phone,
    add_pos_15_job,
    add_pos_15_name,
    add_pos_15_email,
    add_pos_15_phone,
    add_pos_16_job,
    add_pos_16_name,
    add_pos_16_email,
    add_pos_16_phone,
    add_pos_17_job,
    add_pos_17_name,
    add_pos_17_email,
    add_pos_17_phone,
    add_pos_18_job,
    add_pos_18_name,
    add_pos_18_email,
    add_pos_18_phone,
    add_pos_19_job,
    add_pos_19_name,
    add_pos_19_email,
    add_pos_19_phone,
    add_pos_20_job,
    add_pos_20_name,
    add_pos_20_email,
    add_pos_20_phone,
  } = postData;

  useEffect(() => {
    /* Function to fetch and set the crew info */
    const handleMount = async () => {
      try {
        if (host === CLIENT_PROGRAM_HOSTNAME) {
          const { data } = await axiosReq.get(
            `/crewinfonew/${crewInfoData?.id}/`
          );
          console.log(data);
          const {
            production_name,
            production_company,
            company_phone,
            company_email,
            company_address_line_1,
            company_address_line_2,
            company_address_line_3,
            company_address_line_4,
            company_logo,
            total_shoot_days,
            director_name,
            director_email,
            director_phone,
            producer_name,
            producer_email,
            producer_phone,
            pro_coordinator_name,
            pro_coordinator_email,
            pro_coordinator_phone,
            upm_name,
            upm_email,
            upm_phone,
            travel_coordinator_name,
            travel_coordinator_email,
            travel_coordinator_phone,
            production_pa_name,
            production_pa_email,
            production_pa_phone,
            oth_production_pos_1_job,
            oth_production_pos_1_name,
            oth_production_pos_1_email,
            oth_production_pos_1_phone,
            oth_production_pos_2_job,
            oth_production_pos_2_name,
            oth_production_pos_2_email,
            oth_production_pos_2_phone,
            oth_production_pos_3_job,
            oth_production_pos_3_name,
            oth_production_pos_3_email,
            oth_production_pos_3_phone,
            oth_production_pos_4_job,
            oth_production_pos_4_name,
            oth_production_pos_4_email,
            oth_production_pos_4_phone,
            oth_production_pos_5_job,
            oth_production_pos_5_name,
            oth_production_pos_5_email,
            oth_production_pos_5_phone,
            ad_1_name,
            ad_1_email,
            ad_1_phone,
            ad_2_name,
            ad_2_email,
            ad_2_phone,
            ad_3_name,
            ad_3_email,
            ad_3_phone,
            ad_4_name,
            ad_4_email,
            ad_4_phone,
            ad_5_name,
            ad_5_email,
            ad_5_phone,
            pro_assistant_1_name,
            pro_assistant_1_email,
            pro_assistant_1_phone,
            pro_assistant_2_name,
            pro_assistant_2_email,
            pro_assistant_2_phone,
            pro_assistant_3_name,
            pro_assistant_3_email,
            pro_assistant_3_phone,
            pro_assistant_4_name,
            pro_assistant_4_email,
            pro_assistant_4_phone,
            pro_assistant_5_name,
            pro_assistant_5_email,
            pro_assistant_5_phone,
            dop_name,
            dop_email,
            dop_phone,
            camera_operator_name,
            camera_operator_email,
            camera_operator_phone,
            camera_ass_1_name,
            camera_ass_1_email,
            camera_ass_1_phone,
            camera_ass_2_name,
            camera_ass_2_email,
            camera_ass_2_phone,
            dit_name,
            dit_email,
            dit_phone,
            steadicam_name,
            steadicam_email,
            steadicam_phone,
            camera_pa_name,
            camera_pa_email,
            camera_pa_phone,
            oth_camera_pos_1_job,
            oth_camera_pos_1_name,
            oth_camera_pos_1_email,
            oth_camera_pos_1_phone,
            oth_camera_pos_2_job,
            oth_camera_pos_2_name,
            oth_camera_pos_2_email,
            oth_camera_pos_2_phone,
            oth_camera_pos_3_job,
            oth_camera_pos_3_name,
            oth_camera_pos_3_email,
            oth_camera_pos_3_phone,
            oth_camera_pos_4_job,
            oth_camera_pos_4_name,
            oth_camera_pos_4_email,
            oth_camera_pos_4_phone,
            oth_camera_pos_5_job,
            oth_camera_pos_5_name,
            oth_camera_pos_5_email,
            oth_camera_pos_5_phone,
            sound_mixer_name,
            sound_mixer_email,
            sound_mixer_phone,
            boom_operator_name,
            boom_operator_email,
            boom_operator_phone,
            sound_assistant_1_name,
            sound_assistant_1_email,
            sound_assistant_1_phone,
            sound_assistant_2_name,
            sound_assistant_2_email,
            sound_assistant_2_phone,
            script_supervisor_name,
            script_supervisor_email,
            script_supervisor_phone,
            key_hairmakeup_name,
            key_hairmakeup_email,
            key_hairmakeup_phone,
            key_hairstylist_name,
            key_hairstylist_email,
            key_hairstylist_phone,
            sfx_makeup_name,
            sfx_makeup_email,
            sfx_makeup_phone,
            sfx_makeup_assistant_name,
            sfx_makeup_assistant_email,
            sfx_makeup_assistant_phone,
            makeup_artist_1_name,
            makeup_artist_1_email,
            makeup_artist_1_phone,
            makeup_artist_2_name,
            makeup_artist_2_email,
            makeup_artist_2_phone,
            makeup_artist_3_name,
            makeup_artist_3_email,
            makeup_artist_3_phone,
            makeup_artist_4_name,
            makeup_artist_4_email,
            makeup_artist_4_phone,
            makeup_artist_5_name,
            makeup_artist_5_email,
            makeup_artist_5_phone,
            costume_designer_name,
            costume_designer_email,
            costume_designer_phone,
            ass_costume_designer_name,
            ass_costume_designer_email,
            ass_costume_designer_phone,
            wardrobe_assistant_1_name,
            wardrobe_assistant_1_email,
            wardrobe_assistant_1_phone,
            wardrobe_assistant_2_name,
            wardrobe_assistant_2_email,
            wardrobe_assistant_2_phone,
            wardrobe_assistant_3_name,
            wardrobe_assistant_3_email,
            wardrobe_assistant_3_phone,
            wardrobe_assistant_4_name,
            wardrobe_assistant_4_email,
            wardrobe_assistant_4_phone,
            wardrobe_assistant_5_name,
            wardrobe_assistant_5_email,
            wardrobe_assistant_5_phone,
            production_designer_name,
            production_designer_email,
            production_designer_phone,
            art_director_name,
            art_director_email,
            art_director_phone,
            art_assistant_name,
            art_assistant_email,
            art_assistant_phone,
            set_decorator_name,
            set_decorator_email,
            set_decorator_phone,
            set_dresser_name,
            set_dresser_email,
            set_dresser_phone,
            lead_man_name,
            lead_man_email,
            lead_man_phone,
            dresser_name,
            dresser_email,
            dresser_phone,
            prop_master_name,
            prop_master_email,
            prop_master_phone,
            ass_prop_master_name,
            ass_prop_master_email,
            ass_prop_master_phone,
            prop_buyer_name,
            prop_buyer_email,
            prop_buyer_phone,
            armorer_name,
            armorer_email,
            armorer_phone,
            gaffer_name,
            gaffer_email,
            gaffer_phone,
            best_boy_electric_name,
            best_boy_electric_email,
            best_boy_electric_phone,
            electric_3_name,
            electric_3_email,
            electric_3_phone,
            electric_4_name,
            electric_4_email,
            electric_4_phone,
            keygrip_name,
            keygrip_email,
            keygrip_phone,
            best_boy_grip_name,
            best_boy_grip_email,
            best_boy_grip_phone,
            dolly_grip_name,
            dolly_grip_email,
            dolly_grip_phone,
            swing_ge1_name,
            swing_ge1_email,
            swing_ge1_phone,
            swing_ge2_name,
            swing_ge2_email,
            swing_ge2_phone,
            swing_ge3_name,
            swing_ge3_email,
            swing_ge3_phone,
            swing_ge4_name,
            swing_ge4_email,
            swing_ge4_phone,
            swing_ge5_name,
            swing_ge5_email,
            swing_ge5_phone,
            stunt_coordinator_name,
            stunt_coordinator_email,
            stunt_coordinator_phone,
            stunts_1_name,
            stunts_1_email,
            stunts_1_phone,
            stunts_2_name,
            stunts_2_email,
            stunts_2_phone,
            stunts_3_name,
            stunts_3_email,
            stunts_3_phone,
            stunts_4_name,
            stunts_4_email,
            stunts_4_phone,
            stunts_5_name,
            stunts_5_email,
            stunts_5_phone,
            legal_name,
            legal_email,
            legal_phone,
            editor_name,
            editor_email,
            editor_phone,
            set_medic_name,
            set_medic_email,
            set_medic_phone,
            casting_director_name,
            casting_director_email,
            casting_director_phone,
            extras_casting_name,
            extras_casting_email,
            extras_casting_phone,
            writer_name,
            writer_email,
            writer_phone,
            fx_name,
            fx_email,
            fx_phone,
            location_mngr_name,
            location_mngr_email,
            location_mngr_phone,
            location_ass_1_name,
            location_ass_1_email,
            location_ass_1_phone,
            location_ass_2_name,
            location_ass_2_email,
            location_ass_2_phone,
            location_ass_3_name,
            location_ass_3_email,
            location_ass_3_phone,
            location_ass_4_name,
            location_ass_4_email,
            location_ass_4_phone,
            location_security_name,
            location_security_email,
            location_security_phone,
            transport_captain_name,
            transport_captain_email,
            transport_captain_phone,
            transport_manager_1_name,
            transport_manager_1_email,
            transport_manager_1_phone,
            transport_manager_2_name,
            transport_manager_2_email,
            transport_manager_2_phone,
            head_driver_name,
            head_driver_email,
            head_driver_phone,
            car1_name,
            car1_email,
            car1_phone,
            car2_name,
            car2_email,
            car2_phone,
            car3_name,
            car3_email,
            car3_phone,
            truck1_name,
            truck1_email,
            truck1_phone,
            truck2_name,
            truck2_email,
            truck2_phone,
            truck3_name,
            truck3_email,
            truck3_phone,
            catering_co_1_name,
            catering_co_1_email,
            catering_co_1_phone,
            catering_co_2_name,
            catering_co_2_email,
            catering_co_2_phone,
            catering_co_3_name,
            catering_co_3_email,
            catering_co_3_phone,
            craft_service_name,
            craft_service_email,
            craft_service_phone,
            crafty_ass_name,
            crafty_ass_email,
            crafty_ass_phone,
            add_pos_1_job,
            add_pos_1_name,
            add_pos_1_email,
            add_pos_1_phone,
            add_pos_2_job,
            add_pos_2_name,
            add_pos_2_email,
            add_pos_2_phone,
            add_pos_3_job,
            add_pos_3_name,
            add_pos_3_email,
            add_pos_3_phone,
            add_pos_4_job,
            add_pos_4_name,
            add_pos_4_email,
            add_pos_4_phone,
            add_pos_5_job,
            add_pos_5_name,
            add_pos_5_email,
            add_pos_5_phone,
            add_pos_6_job,
            add_pos_6_name,
            add_pos_6_email,
            add_pos_6_phone,
            add_pos_7_job,
            add_pos_7_name,
            add_pos_7_email,
            add_pos_7_phone,
            add_pos_8_job,
            add_pos_8_name,
            add_pos_8_email,
            add_pos_8_phone,
            add_pos_9_job,
            add_pos_9_name,
            add_pos_9_email,
            add_pos_9_phone,
            add_pos_10_job,
            add_pos_10_name,
            add_pos_10_email,
            add_pos_10_phone,
            add_pos_11_job,
            add_pos_11_name,
            add_pos_11_email,
            add_pos_11_phone,
            add_pos_12_job,
            add_pos_12_name,
            add_pos_12_email,
            add_pos_12_phone,
            add_pos_13_job,
            add_pos_13_name,
            add_pos_13_email,
            add_pos_13_phone,
            add_pos_14_job,
            add_pos_14_name,
            add_pos_14_email,
            add_pos_14_phone,
            add_pos_15_job,
            add_pos_15_name,
            add_pos_15_email,
            add_pos_15_phone,
            add_pos_16_job,
            add_pos_16_name,
            add_pos_16_email,
            add_pos_16_phone,
            add_pos_17_job,
            add_pos_17_name,
            add_pos_17_email,
            add_pos_17_phone,
            add_pos_18_job,
            add_pos_18_name,
            add_pos_18_email,
            add_pos_18_phone,
            add_pos_19_job,
            add_pos_19_name,
            add_pos_19_email,
            add_pos_19_phone,
            add_pos_20_job,
            add_pos_20_name,
            add_pos_20_email,
            add_pos_20_phone,
          } = data;

          applyCrewUserIdsFromApi(data);

          setPostData({
            production_name,
            production_company,
            company_phone,
            company_email,
            company_address_line_1,
            company_address_line_2,
            company_address_line_3,
            company_address_line_4,
            company_logo,
            total_shoot_days,
            director_name,
            director_email,
            director_phone,
            producer_name,
            producer_email,
            producer_phone,
            pro_coordinator_name,
            pro_coordinator_email,
            pro_coordinator_phone,
            upm_name,
            upm_email,
            upm_phone,
            travel_coordinator_name,
            travel_coordinator_email,
            travel_coordinator_phone,
            production_pa_name,
            production_pa_email,
            production_pa_phone,
            oth_production_pos_1_job,
            oth_production_pos_1_name,
            oth_production_pos_1_email,
            oth_production_pos_1_phone,
            oth_production_pos_2_job,
            oth_production_pos_2_name,
            oth_production_pos_2_email,
            oth_production_pos_2_phone,
            oth_production_pos_3_job,
            oth_production_pos_3_name,
            oth_production_pos_3_email,
            oth_production_pos_3_phone,
            oth_production_pos_4_job,
            oth_production_pos_4_name,
            oth_production_pos_4_email,
            oth_production_pos_4_phone,
            oth_production_pos_5_job,
            oth_production_pos_5_name,
            oth_production_pos_5_email,
            oth_production_pos_5_phone,
            ad_1_name,
            ad_1_email,
            ad_1_phone,
            ad_2_name,
            ad_2_email,
            ad_2_phone,
            ad_3_name,
            ad_3_email,
            ad_3_phone,
            ad_4_name,
            ad_4_email,
            ad_4_phone,
            ad_5_name,
            ad_5_email,
            ad_5_phone,
            pro_assistant_1_name,
            pro_assistant_1_email,
            pro_assistant_1_phone,
            pro_assistant_2_name,
            pro_assistant_2_email,
            pro_assistant_2_phone,
            pro_assistant_3_name,
            pro_assistant_3_email,
            pro_assistant_3_phone,
            pro_assistant_4_name,
            pro_assistant_4_email,
            pro_assistant_4_phone,
            pro_assistant_5_name,
            pro_assistant_5_email,
            pro_assistant_5_phone,
            dop_name,
            dop_email,
            dop_phone,
            camera_operator_name,
            camera_operator_email,
            camera_operator_phone,
            camera_ass_1_name,
            camera_ass_1_email,
            camera_ass_1_phone,
            camera_ass_2_name,
            camera_ass_2_email,
            camera_ass_2_phone,
            dit_name,
            dit_email,
            dit_phone,
            steadicam_name,
            steadicam_email,
            steadicam_phone,
            camera_pa_name,
            camera_pa_email,
            camera_pa_phone,
            oth_camera_pos_1_job,
            oth_camera_pos_1_name,
            oth_camera_pos_1_email,
            oth_camera_pos_1_phone,
            oth_camera_pos_2_job,
            oth_camera_pos_2_name,
            oth_camera_pos_2_email,
            oth_camera_pos_2_phone,
            oth_camera_pos_3_job,
            oth_camera_pos_3_name,
            oth_camera_pos_3_email,
            oth_camera_pos_3_phone,
            oth_camera_pos_4_job,
            oth_camera_pos_4_name,
            oth_camera_pos_4_email,
            oth_camera_pos_4_phone,
            oth_camera_pos_5_job,
            oth_camera_pos_5_name,
            oth_camera_pos_5_email,
            oth_camera_pos_5_phone,
            sound_mixer_name,
            sound_mixer_email,
            sound_mixer_phone,
            boom_operator_name,
            boom_operator_email,
            boom_operator_phone,
            sound_assistant_1_name,
            sound_assistant_1_email,
            sound_assistant_1_phone,
            sound_assistant_2_name,
            sound_assistant_2_email,
            sound_assistant_2_phone,
            script_supervisor_name,
            script_supervisor_email,
            script_supervisor_phone,
            key_hairmakeup_name,
            key_hairmakeup_email,
            key_hairmakeup_phone,
            key_hairstylist_name,
            key_hairstylist_email,
            key_hairstylist_phone,
            sfx_makeup_name,
            sfx_makeup_email,
            sfx_makeup_phone,
            sfx_makeup_assistant_name,
            sfx_makeup_assistant_email,
            sfx_makeup_assistant_phone,
            makeup_artist_1_name,
            makeup_artist_1_email,
            makeup_artist_1_phone,
            makeup_artist_2_name,
            makeup_artist_2_email,
            makeup_artist_2_phone,
            makeup_artist_3_name,
            makeup_artist_3_email,
            makeup_artist_3_phone,
            makeup_artist_4_name,
            makeup_artist_4_email,
            makeup_artist_4_phone,
            makeup_artist_5_name,
            makeup_artist_5_email,
            makeup_artist_5_phone,
            costume_designer_name,
            costume_designer_email,
            costume_designer_phone,
            ass_costume_designer_name,
            ass_costume_designer_email,
            ass_costume_designer_phone,
            wardrobe_assistant_1_name,
            wardrobe_assistant_1_email,
            wardrobe_assistant_1_phone,
            wardrobe_assistant_2_name,
            wardrobe_assistant_2_email,
            wardrobe_assistant_2_phone,
            wardrobe_assistant_3_name,
            wardrobe_assistant_3_email,
            wardrobe_assistant_3_phone,
            wardrobe_assistant_4_name,
            wardrobe_assistant_4_email,
            wardrobe_assistant_4_phone,
            wardrobe_assistant_5_name,
            wardrobe_assistant_5_email,
            wardrobe_assistant_5_phone,
            production_designer_name,
            production_designer_email,
            production_designer_phone,
            art_director_name,
            art_director_email,
            art_director_phone,
            art_assistant_name,
            art_assistant_email,
            art_assistant_phone,
            set_decorator_name,
            set_decorator_email,
            set_decorator_phone,
            set_dresser_name,
            set_dresser_email,
            set_dresser_phone,
            lead_man_name,
            lead_man_email,
            lead_man_phone,
            dresser_name,
            dresser_email,
            dresser_phone,
            prop_master_name,
            prop_master_email,
            prop_master_phone,
            ass_prop_master_name,
            ass_prop_master_email,
            ass_prop_master_phone,
            prop_buyer_name,
            prop_buyer_email,
            prop_buyer_phone,
            armorer_name,
            armorer_email,
            armorer_phone,
            gaffer_name,
            gaffer_email,
            gaffer_phone,
            best_boy_electric_name,
            best_boy_electric_email,
            best_boy_electric_phone,
            electric_3_name,
            electric_3_email,
            electric_3_phone,
            electric_4_name,
            electric_4_email,
            electric_4_phone,
            keygrip_name,
            keygrip_email,
            keygrip_phone,
            best_boy_grip_name,
            best_boy_grip_email,
            best_boy_grip_phone,
            dolly_grip_name,
            dolly_grip_email,
            dolly_grip_phone,
            swing_ge1_name,
            swing_ge1_email,
            swing_ge1_phone,
            swing_ge2_name,
            swing_ge2_email,
            swing_ge2_phone,
            swing_ge3_name,
            swing_ge3_email,
            swing_ge3_phone,
            swing_ge4_name,
            swing_ge4_email,
            swing_ge4_phone,
            swing_ge5_name,
            swing_ge5_email,
            swing_ge5_phone,
            stunt_coordinator_name,
            stunt_coordinator_email,
            stunt_coordinator_phone,
            stunts_1_name,
            stunts_1_email,
            stunts_1_phone,
            stunts_2_name,
            stunts_2_email,
            stunts_2_phone,
            stunts_3_name,
            stunts_3_email,
            stunts_3_phone,
            stunts_4_name,
            stunts_4_email,
            stunts_4_phone,
            stunts_5_name,
            stunts_5_email,
            stunts_5_phone,
            legal_name,
            legal_email,
            legal_phone,
            editor_name,
            editor_email,
            editor_phone,
            set_medic_name,
            set_medic_email,
            set_medic_phone,
            casting_director_name,
            casting_director_email,
            casting_director_phone,
            extras_casting_name,
            extras_casting_email,
            extras_casting_phone,
            writer_name,
            writer_email,
            writer_phone,
            fx_name,
            fx_email,
            fx_phone,
            location_mngr_name,
            location_mngr_email,
            location_mngr_phone,
            location_ass_1_name,
            location_ass_1_email,
            location_ass_1_phone,
            location_ass_2_name,
            location_ass_2_email,
            location_ass_2_phone,
            location_ass_3_name,
            location_ass_3_email,
            location_ass_3_phone,
            location_ass_4_name,
            location_ass_4_email,
            location_ass_4_phone,
            location_security_name,
            location_security_email,
            location_security_phone,
            transport_captain_name,
            transport_captain_email,
            transport_captain_phone,
            transport_manager_1_name,
            transport_manager_1_email,
            transport_manager_1_phone,
            transport_manager_2_name,
            transport_manager_2_email,
            transport_manager_2_phone,
            head_driver_name,
            head_driver_email,
            head_driver_phone,
            car1_name,
            car1_email,
            car1_phone,
            car2_name,
            car2_email,
            car2_phone,
            car3_name,
            car3_email,
            car3_phone,
            truck1_name,
            truck1_email,
            truck1_phone,
            truck2_name,
            truck2_email,
            truck2_phone,
            truck3_name,
            truck3_email,
            truck3_phone,
            catering_co_1_name,
            catering_co_1_email,
            catering_co_1_phone,
            catering_co_2_name,
            catering_co_2_email,
            catering_co_2_phone,
            catering_co_3_name,
            catering_co_3_email,
            catering_co_3_phone,
            craft_service_name,
            craft_service_email,
            craft_service_phone,
            crafty_ass_name,
            crafty_ass_email,
            crafty_ass_phone,
            add_pos_1_job,
            add_pos_1_name,
            add_pos_1_email,
            add_pos_1_phone,
            add_pos_2_job,
            add_pos_2_name,
            add_pos_2_email,
            add_pos_2_phone,
            add_pos_3_job,
            add_pos_3_name,
            add_pos_3_email,
            add_pos_3_phone,
            add_pos_4_job,
            add_pos_4_name,
            add_pos_4_email,
            add_pos_4_phone,
            add_pos_5_job,
            add_pos_5_name,
            add_pos_5_email,
            add_pos_5_phone,
            add_pos_6_job,
            add_pos_6_name,
            add_pos_6_email,
            add_pos_6_phone,
            add_pos_7_job,
            add_pos_7_name,
            add_pos_7_email,
            add_pos_7_phone,
            add_pos_8_job,
            add_pos_8_name,
            add_pos_8_email,
            add_pos_8_phone,
            add_pos_9_job,
            add_pos_9_name,
            add_pos_9_email,
            add_pos_9_phone,
            add_pos_10_job,
            add_pos_10_name,
            add_pos_10_email,
            add_pos_10_phone,
            add_pos_11_job,
            add_pos_11_name,
            add_pos_11_email,
            add_pos_11_phone,
            add_pos_12_job,
            add_pos_12_name,
            add_pos_12_email,
            add_pos_12_phone,
            add_pos_13_job,
            add_pos_13_name,
            add_pos_13_email,
            add_pos_13_phone,
            add_pos_14_job,
            add_pos_14_name,
            add_pos_14_email,
            add_pos_14_phone,
            add_pos_15_job,
            add_pos_15_name,
            add_pos_15_email,
            add_pos_15_phone,
            add_pos_16_job,
            add_pos_16_name,
            add_pos_16_email,
            add_pos_16_phone,
            add_pos_17_job,
            add_pos_17_name,
            add_pos_17_email,
            add_pos_17_phone,
            add_pos_18_job,
            add_pos_18_name,
            add_pos_18_email,
            add_pos_18_phone,
            add_pos_19_job,
            add_pos_19_name,
            add_pos_19_email,
            add_pos_19_phone,
            add_pos_20_job,
            add_pos_20_name,
            add_pos_20_email,
            add_pos_20_phone,
          });
        } else {
          const { data } = await axiosInstance.get(
            `${localStorage.getItem("projectSlug")}/crewinfonew/${
              crewInfoData?.id
            }/`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              },
              withCredentials: true,
            }
          );
          const {
            production_name,
            production_company,
            company_phone,
            company_email,
            company_address_line_1,
            company_address_line_2,
            company_address_line_3,
            company_address_line_4,
            company_logo,
            total_shoot_days,
            director_name,
            director_email,
            director_phone,
            producer_name,
            producer_email,
            producer_phone,
            pro_coordinator_name,
            pro_coordinator_email,
            pro_coordinator_phone,
            upm_name,
            upm_email,
            upm_phone,
            travel_coordinator_name,
            travel_coordinator_email,
            travel_coordinator_phone,
            production_pa_name,
            production_pa_email,
            production_pa_phone,
            oth_production_pos_1_job,
            oth_production_pos_1_name,
            oth_production_pos_1_email,
            oth_production_pos_1_phone,
            oth_production_pos_2_job,
            oth_production_pos_2_name,
            oth_production_pos_2_email,
            oth_production_pos_2_phone,
            oth_production_pos_3_job,
            oth_production_pos_3_name,
            oth_production_pos_3_email,
            oth_production_pos_3_phone,
            oth_production_pos_4_job,
            oth_production_pos_4_name,
            oth_production_pos_4_email,
            oth_production_pos_4_phone,
            oth_production_pos_5_job,
            oth_production_pos_5_name,
            oth_production_pos_5_email,
            oth_production_pos_5_phone,
            ad_1_name,
            ad_1_email,
            ad_1_phone,
            ad_2_name,
            ad_2_email,
            ad_2_phone,
            ad_3_name,
            ad_3_email,
            ad_3_phone,
            ad_4_name,
            ad_4_email,
            ad_4_phone,
            ad_5_name,
            ad_5_email,
            ad_5_phone,
            pro_assistant_1_name,
            pro_assistant_1_email,
            pro_assistant_1_phone,
            pro_assistant_2_name,
            pro_assistant_2_email,
            pro_assistant_2_phone,
            pro_assistant_3_name,
            pro_assistant_3_email,
            pro_assistant_3_phone,
            pro_assistant_4_name,
            pro_assistant_4_email,
            pro_assistant_4_phone,
            pro_assistant_5_name,
            pro_assistant_5_email,
            pro_assistant_5_phone,
            dop_name,
            dop_email,
            dop_phone,
            camera_operator_name,
            camera_operator_email,
            camera_operator_phone,
            camera_ass_1_name,
            camera_ass_1_email,
            camera_ass_1_phone,
            camera_ass_2_name,
            camera_ass_2_email,
            camera_ass_2_phone,
            dit_name,
            dit_email,
            dit_phone,
            steadicam_name,
            steadicam_email,
            steadicam_phone,
            camera_pa_name,
            camera_pa_email,
            camera_pa_phone,
            oth_camera_pos_1_job,
            oth_camera_pos_1_name,
            oth_camera_pos_1_email,
            oth_camera_pos_1_phone,
            oth_camera_pos_2_job,
            oth_camera_pos_2_name,
            oth_camera_pos_2_email,
            oth_camera_pos_2_phone,
            oth_camera_pos_3_job,
            oth_camera_pos_3_name,
            oth_camera_pos_3_email,
            oth_camera_pos_3_phone,
            oth_camera_pos_4_job,
            oth_camera_pos_4_name,
            oth_camera_pos_4_email,
            oth_camera_pos_4_phone,
            oth_camera_pos_5_job,
            oth_camera_pos_5_name,
            oth_camera_pos_5_email,
            oth_camera_pos_5_phone,
            sound_mixer_name,
            sound_mixer_email,
            sound_mixer_phone,
            boom_operator_name,
            boom_operator_email,
            boom_operator_phone,
            sound_assistant_1_name,
            sound_assistant_1_email,
            sound_assistant_1_phone,
            sound_assistant_2_name,
            sound_assistant_2_email,
            sound_assistant_2_phone,
            script_supervisor_name,
            script_supervisor_email,
            script_supervisor_phone,
            key_hairmakeup_name,
            key_hairmakeup_email,
            key_hairmakeup_phone,
            key_hairstylist_name,
            key_hairstylist_email,
            key_hairstylist_phone,
            sfx_makeup_name,
            sfx_makeup_email,
            sfx_makeup_phone,
            sfx_makeup_assistant_name,
            sfx_makeup_assistant_email,
            sfx_makeup_assistant_phone,
            makeup_artist_1_name,
            makeup_artist_1_email,
            makeup_artist_1_phone,
            makeup_artist_2_name,
            makeup_artist_2_email,
            makeup_artist_2_phone,
            makeup_artist_3_name,
            makeup_artist_3_email,
            makeup_artist_3_phone,
            makeup_artist_4_name,
            makeup_artist_4_email,
            makeup_artist_4_phone,
            makeup_artist_5_name,
            makeup_artist_5_email,
            makeup_artist_5_phone,
            costume_designer_name,
            costume_designer_email,
            costume_designer_phone,
            ass_costume_designer_name,
            ass_costume_designer_email,
            ass_costume_designer_phone,
            wardrobe_assistant_1_name,
            wardrobe_assistant_1_email,
            wardrobe_assistant_1_phone,
            wardrobe_assistant_2_name,
            wardrobe_assistant_2_email,
            wardrobe_assistant_2_phone,
            wardrobe_assistant_3_name,
            wardrobe_assistant_3_email,
            wardrobe_assistant_3_phone,
            wardrobe_assistant_4_name,
            wardrobe_assistant_4_email,
            wardrobe_assistant_4_phone,
            wardrobe_assistant_5_name,
            wardrobe_assistant_5_email,
            wardrobe_assistant_5_phone,
            production_designer_name,
            production_designer_email,
            production_designer_phone,
            art_director_name,
            art_director_email,
            art_director_phone,
            art_assistant_name,
            art_assistant_email,
            art_assistant_phone,
            set_decorator_name,
            set_decorator_email,
            set_decorator_phone,
            set_dresser_name,
            set_dresser_email,
            set_dresser_phone,
            lead_man_name,
            lead_man_email,
            lead_man_phone,
            dresser_name,
            dresser_email,
            dresser_phone,
            prop_master_name,
            prop_master_email,
            prop_master_phone,
            ass_prop_master_name,
            ass_prop_master_email,
            ass_prop_master_phone,
            prop_buyer_name,
            prop_buyer_email,
            prop_buyer_phone,
            armorer_name,
            armorer_email,
            armorer_phone,
            gaffer_name,
            gaffer_email,
            gaffer_phone,
            best_boy_electric_name,
            best_boy_electric_email,
            best_boy_electric_phone,
            electric_3_name,
            electric_3_email,
            electric_3_phone,
            electric_4_name,
            electric_4_email,
            electric_4_phone,
            keygrip_name,
            keygrip_email,
            keygrip_phone,
            best_boy_grip_name,
            best_boy_grip_email,
            best_boy_grip_phone,
            dolly_grip_name,
            dolly_grip_email,
            dolly_grip_phone,
            swing_ge1_name,
            swing_ge1_email,
            swing_ge1_phone,
            swing_ge2_name,
            swing_ge2_email,
            swing_ge2_phone,
            swing_ge3_name,
            swing_ge3_email,
            swing_ge3_phone,
            swing_ge4_name,
            swing_ge4_email,
            swing_ge4_phone,
            swing_ge5_name,
            swing_ge5_email,
            swing_ge5_phone,
            stunt_coordinator_name,
            stunt_coordinator_email,
            stunt_coordinator_phone,
            stunts_1_name,
            stunts_1_email,
            stunts_1_phone,
            stunts_2_name,
            stunts_2_email,
            stunts_2_phone,
            stunts_3_name,
            stunts_3_email,
            stunts_3_phone,
            stunts_4_name,
            stunts_4_email,
            stunts_4_phone,
            stunts_5_name,
            stunts_5_email,
            stunts_5_phone,
            legal_name,
            legal_email,
            legal_phone,
            editor_name,
            editor_email,
            editor_phone,
            set_medic_name,
            set_medic_email,
            set_medic_phone,
            casting_director_name,
            casting_director_email,
            casting_director_phone,
            extras_casting_name,
            extras_casting_email,
            extras_casting_phone,
            writer_name,
            writer_email,
            writer_phone,
            fx_name,
            fx_email,
            fx_phone,
            location_mngr_name,
            location_mngr_email,
            location_mngr_phone,
            location_ass_1_name,
            location_ass_1_email,
            location_ass_1_phone,
            location_ass_2_name,
            location_ass_2_email,
            location_ass_2_phone,
            location_ass_3_name,
            location_ass_3_email,
            location_ass_3_phone,
            location_ass_4_name,
            location_ass_4_email,
            location_ass_4_phone,
            location_security_name,
            location_security_email,
            location_security_phone,
            transport_captain_name,
            transport_captain_email,
            transport_captain_phone,
            transport_manager_1_name,
            transport_manager_1_email,
            transport_manager_1_phone,
            transport_manager_2_name,
            transport_manager_2_email,
            transport_manager_2_phone,
            head_driver_name,
            head_driver_email,
            head_driver_phone,
            car1_name,
            car1_email,
            car1_phone,
            car2_name,
            car2_email,
            car2_phone,
            car3_name,
            car3_email,
            car3_phone,
            truck1_name,
            truck1_email,
            truck1_phone,
            truck2_name,
            truck2_email,
            truck2_phone,
            truck3_name,
            truck3_email,
            truck3_phone,
            catering_co_1_name,
            catering_co_1_email,
            catering_co_1_phone,
            catering_co_2_name,
            catering_co_2_email,
            catering_co_2_phone,
            catering_co_3_name,
            catering_co_3_email,
            catering_co_3_phone,
            craft_service_name,
            craft_service_email,
            craft_service_phone,
            crafty_ass_name,
            crafty_ass_email,
            crafty_ass_phone,
            add_pos_1_job,
            add_pos_1_name,
            add_pos_1_email,
            add_pos_1_phone,
            add_pos_2_job,
            add_pos_2_name,
            add_pos_2_email,
            add_pos_2_phone,
            add_pos_3_job,
            add_pos_3_name,
            add_pos_3_email,
            add_pos_3_phone,
            add_pos_4_job,
            add_pos_4_name,
            add_pos_4_email,
            add_pos_4_phone,
            add_pos_5_job,
            add_pos_5_name,
            add_pos_5_email,
            add_pos_5_phone,
            add_pos_6_job,
            add_pos_6_name,
            add_pos_6_email,
            add_pos_6_phone,
            add_pos_7_job,
            add_pos_7_name,
            add_pos_7_email,
            add_pos_7_phone,
            add_pos_8_job,
            add_pos_8_name,
            add_pos_8_email,
            add_pos_8_phone,
            add_pos_9_job,
            add_pos_9_name,
            add_pos_9_email,
            add_pos_9_phone,
            add_pos_10_job,
            add_pos_10_name,
            add_pos_10_email,
            add_pos_10_phone,
            add_pos_11_job,
            add_pos_11_name,
            add_pos_11_email,
            add_pos_11_phone,
            add_pos_12_job,
            add_pos_12_name,
            add_pos_12_email,
            add_pos_12_phone,
            add_pos_13_job,
            add_pos_13_name,
            add_pos_13_email,
            add_pos_13_phone,
            add_pos_14_job,
            add_pos_14_name,
            add_pos_14_email,
            add_pos_14_phone,
            add_pos_15_job,
            add_pos_15_name,
            add_pos_15_email,
            add_pos_15_phone,
            add_pos_16_job,
            add_pos_16_name,
            add_pos_16_email,
            add_pos_16_phone,
            add_pos_17_job,
            add_pos_17_name,
            add_pos_17_email,
            add_pos_17_phone,
            add_pos_18_job,
            add_pos_18_name,
            add_pos_18_email,
            add_pos_18_phone,
            add_pos_19_job,
            add_pos_19_name,
            add_pos_19_email,
            add_pos_19_phone,
            add_pos_20_job,
            add_pos_20_name,
            add_pos_20_email,
            add_pos_20_phone,
          } = data;

          applyCrewUserIdsFromApi(data);

          setPostData({
            production_name,
            production_company,
            company_phone,
            company_email,
            company_address_line_1,
            company_address_line_2,
            company_address_line_3,
            company_address_line_4,
            company_logo,
            total_shoot_days,
            director_name,
            director_email,
            director_phone,
            producer_name,
            producer_email,
            producer_phone,
            pro_coordinator_name,
            pro_coordinator_email,
            pro_coordinator_phone,
            upm_name,
            upm_email,
            upm_phone,
            travel_coordinator_name,
            travel_coordinator_email,
            travel_coordinator_phone,
            production_pa_name,
            production_pa_email,
            production_pa_phone,
            oth_production_pos_1_job,
            oth_production_pos_1_name,
            oth_production_pos_1_email,
            oth_production_pos_1_phone,
            oth_production_pos_2_job,
            oth_production_pos_2_name,
            oth_production_pos_2_email,
            oth_production_pos_2_phone,
            oth_production_pos_3_job,
            oth_production_pos_3_name,
            oth_production_pos_3_email,
            oth_production_pos_3_phone,
            oth_production_pos_4_job,
            oth_production_pos_4_name,
            oth_production_pos_4_email,
            oth_production_pos_4_phone,
            oth_production_pos_5_job,
            oth_production_pos_5_name,
            oth_production_pos_5_email,
            oth_production_pos_5_phone,
            ad_1_name,
            ad_1_email,
            ad_1_phone,
            ad_2_name,
            ad_2_email,
            ad_2_phone,
            ad_3_name,
            ad_3_email,
            ad_3_phone,
            ad_4_name,
            ad_4_email,
            ad_4_phone,
            ad_5_name,
            ad_5_email,
            ad_5_phone,
            pro_assistant_1_name,
            pro_assistant_1_email,
            pro_assistant_1_phone,
            pro_assistant_2_name,
            pro_assistant_2_email,
            pro_assistant_2_phone,
            pro_assistant_3_name,
            pro_assistant_3_email,
            pro_assistant_3_phone,
            pro_assistant_4_name,
            pro_assistant_4_email,
            pro_assistant_4_phone,
            pro_assistant_5_name,
            pro_assistant_5_email,
            pro_assistant_5_phone,
            dop_name,
            dop_email,
            dop_phone,
            camera_operator_name,
            camera_operator_email,
            camera_operator_phone,
            camera_ass_1_name,
            camera_ass_1_email,
            camera_ass_1_phone,
            camera_ass_2_name,
            camera_ass_2_email,
            camera_ass_2_phone,
            dit_name,
            dit_email,
            dit_phone,
            steadicam_name,
            steadicam_email,
            steadicam_phone,
            camera_pa_name,
            camera_pa_email,
            camera_pa_phone,
            oth_camera_pos_1_job,
            oth_camera_pos_1_name,
            oth_camera_pos_1_email,
            oth_camera_pos_1_phone,
            oth_camera_pos_2_job,
            oth_camera_pos_2_name,
            oth_camera_pos_2_email,
            oth_camera_pos_2_phone,
            oth_camera_pos_3_job,
            oth_camera_pos_3_name,
            oth_camera_pos_3_email,
            oth_camera_pos_3_phone,
            oth_camera_pos_4_job,
            oth_camera_pos_4_name,
            oth_camera_pos_4_email,
            oth_camera_pos_4_phone,
            oth_camera_pos_5_job,
            oth_camera_pos_5_name,
            oth_camera_pos_5_email,
            oth_camera_pos_5_phone,
            sound_mixer_name,
            sound_mixer_email,
            sound_mixer_phone,
            boom_operator_name,
            boom_operator_email,
            boom_operator_phone,
            sound_assistant_1_name,
            sound_assistant_1_email,
            sound_assistant_1_phone,
            sound_assistant_2_name,
            sound_assistant_2_email,
            sound_assistant_2_phone,
            script_supervisor_name,
            script_supervisor_email,
            script_supervisor_phone,
            key_hairmakeup_name,
            key_hairmakeup_email,
            key_hairmakeup_phone,
            key_hairstylist_name,
            key_hairstylist_email,
            key_hairstylist_phone,
            sfx_makeup_name,
            sfx_makeup_email,
            sfx_makeup_phone,
            sfx_makeup_assistant_name,
            sfx_makeup_assistant_email,
            sfx_makeup_assistant_phone,
            makeup_artist_1_name,
            makeup_artist_1_email,
            makeup_artist_1_phone,
            makeup_artist_2_name,
            makeup_artist_2_email,
            makeup_artist_2_phone,
            makeup_artist_3_name,
            makeup_artist_3_email,
            makeup_artist_3_phone,
            makeup_artist_4_name,
            makeup_artist_4_email,
            makeup_artist_4_phone,
            makeup_artist_5_name,
            makeup_artist_5_email,
            makeup_artist_5_phone,
            costume_designer_name,
            costume_designer_email,
            costume_designer_phone,
            ass_costume_designer_name,
            ass_costume_designer_email,
            ass_costume_designer_phone,
            wardrobe_assistant_1_name,
            wardrobe_assistant_1_email,
            wardrobe_assistant_1_phone,
            wardrobe_assistant_2_name,
            wardrobe_assistant_2_email,
            wardrobe_assistant_2_phone,
            wardrobe_assistant_3_name,
            wardrobe_assistant_3_email,
            wardrobe_assistant_3_phone,
            wardrobe_assistant_4_name,
            wardrobe_assistant_4_email,
            wardrobe_assistant_4_phone,
            wardrobe_assistant_5_name,
            wardrobe_assistant_5_email,
            wardrobe_assistant_5_phone,
            production_designer_name,
            production_designer_email,
            production_designer_phone,
            art_director_name,
            art_director_email,
            art_director_phone,
            art_assistant_name,
            art_assistant_email,
            art_assistant_phone,
            set_decorator_name,
            set_decorator_email,
            set_decorator_phone,
            set_dresser_name,
            set_dresser_email,
            set_dresser_phone,
            lead_man_name,
            lead_man_email,
            lead_man_phone,
            dresser_name,
            dresser_email,
            dresser_phone,
            prop_master_name,
            prop_master_email,
            prop_master_phone,
            ass_prop_master_name,
            ass_prop_master_email,
            ass_prop_master_phone,
            prop_buyer_name,
            prop_buyer_email,
            prop_buyer_phone,
            armorer_name,
            armorer_email,
            armorer_phone,
            gaffer_name,
            gaffer_email,
            gaffer_phone,
            best_boy_electric_name,
            best_boy_electric_email,
            best_boy_electric_phone,
            electric_3_name,
            electric_3_email,
            electric_3_phone,
            electric_4_name,
            electric_4_email,
            electric_4_phone,
            keygrip_name,
            keygrip_email,
            keygrip_phone,
            best_boy_grip_name,
            best_boy_grip_email,
            best_boy_grip_phone,
            dolly_grip_name,
            dolly_grip_email,
            dolly_grip_phone,
            swing_ge1_name,
            swing_ge1_email,
            swing_ge1_phone,
            swing_ge2_name,
            swing_ge2_email,
            swing_ge2_phone,
            swing_ge3_name,
            swing_ge3_email,
            swing_ge3_phone,
            swing_ge4_name,
            swing_ge4_email,
            swing_ge4_phone,
            swing_ge5_name,
            swing_ge5_email,
            swing_ge5_phone,
            stunt_coordinator_name,
            stunt_coordinator_email,
            stunt_coordinator_phone,
            stunts_1_name,
            stunts_1_email,
            stunts_1_phone,
            stunts_2_name,
            stunts_2_email,
            stunts_2_phone,
            stunts_3_name,
            stunts_3_email,
            stunts_3_phone,
            stunts_4_name,
            stunts_4_email,
            stunts_4_phone,
            stunts_5_name,
            stunts_5_email,
            stunts_5_phone,
            legal_name,
            legal_email,
            legal_phone,
            editor_name,
            editor_email,
            editor_phone,
            set_medic_name,
            set_medic_email,
            set_medic_phone,
            casting_director_name,
            casting_director_email,
            casting_director_phone,
            extras_casting_name,
            extras_casting_email,
            extras_casting_phone,
            writer_name,
            writer_email,
            writer_phone,
            fx_name,
            fx_email,
            fx_phone,
            location_mngr_name,
            location_mngr_email,
            location_mngr_phone,
            location_ass_1_name,
            location_ass_1_email,
            location_ass_1_phone,
            location_ass_2_name,
            location_ass_2_email,
            location_ass_2_phone,
            location_ass_3_name,
            location_ass_3_email,
            location_ass_3_phone,
            location_ass_4_name,
            location_ass_4_email,
            location_ass_4_phone,
            location_security_name,
            location_security_email,
            location_security_phone,
            transport_captain_name,
            transport_captain_email,
            transport_captain_phone,
            transport_manager_1_name,
            transport_manager_1_email,
            transport_manager_1_phone,
            transport_manager_2_name,
            transport_manager_2_email,
            transport_manager_2_phone,
            head_driver_name,
            head_driver_email,
            head_driver_phone,
            car1_name,
            car1_email,
            car1_phone,
            car2_name,
            car2_email,
            car2_phone,
            car3_name,
            car3_email,
            car3_phone,
            truck1_name,
            truck1_email,
            truck1_phone,
            truck2_name,
            truck2_email,
            truck2_phone,
            truck3_name,
            truck3_email,
            truck3_phone,
            catering_co_1_name,
            catering_co_1_email,
            catering_co_1_phone,
            catering_co_2_name,
            catering_co_2_email,
            catering_co_2_phone,
            catering_co_3_name,
            catering_co_3_email,
            catering_co_3_phone,
            craft_service_name,
            craft_service_email,
            craft_service_phone,
            crafty_ass_name,
            crafty_ass_email,
            crafty_ass_phone,
            add_pos_1_job,
            add_pos_1_name,
            add_pos_1_email,
            add_pos_1_phone,
            add_pos_2_job,
            add_pos_2_name,
            add_pos_2_email,
            add_pos_2_phone,
            add_pos_3_job,
            add_pos_3_name,
            add_pos_3_email,
            add_pos_3_phone,
            add_pos_4_job,
            add_pos_4_name,
            add_pos_4_email,
            add_pos_4_phone,
            add_pos_5_job,
            add_pos_5_name,
            add_pos_5_email,
            add_pos_5_phone,
            add_pos_6_job,
            add_pos_6_name,
            add_pos_6_email,
            add_pos_6_phone,
            add_pos_7_job,
            add_pos_7_name,
            add_pos_7_email,
            add_pos_7_phone,
            add_pos_8_job,
            add_pos_8_name,
            add_pos_8_email,
            add_pos_8_phone,
            add_pos_9_job,
            add_pos_9_name,
            add_pos_9_email,
            add_pos_9_phone,
            add_pos_10_job,
            add_pos_10_name,
            add_pos_10_email,
            add_pos_10_phone,
            add_pos_11_job,
            add_pos_11_name,
            add_pos_11_email,
            add_pos_11_phone,
            add_pos_12_job,
            add_pos_12_name,
            add_pos_12_email,
            add_pos_12_phone,
            add_pos_13_job,
            add_pos_13_name,
            add_pos_13_email,
            add_pos_13_phone,
            add_pos_14_job,
            add_pos_14_name,
            add_pos_14_email,
            add_pos_14_phone,
            add_pos_15_job,
            add_pos_15_name,
            add_pos_15_email,
            add_pos_15_phone,
            add_pos_16_job,
            add_pos_16_name,
            add_pos_16_email,
            add_pos_16_phone,
            add_pos_17_job,
            add_pos_17_name,
            add_pos_17_email,
            add_pos_17_phone,
            add_pos_18_job,
            add_pos_18_name,
            add_pos_18_email,
            add_pos_18_phone,
            add_pos_19_job,
            add_pos_19_name,
            add_pos_19_email,
            add_pos_19_phone,
            add_pos_20_job,
            add_pos_20_name,
            add_pos_20_email,
            add_pos_20_phone,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };

    if (host === CLIENT_PROGRAM_HOSTNAME) {
      handleMount();
    } else {
      if (localStorage.getItem("accessToken")) {
        handleMount();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCrewOptions = useCallback(
    async (searchValue = "") => {
      try {
        setCrewSearchLoading(true);
        let data;
        const path = buildCrewUsersUrl(searchValue);

        if (host === CLIENT_PROGRAM_HOSTNAME) {
          const response = await axiosReq.get(path);
          if (response?.success === false || response?.status === false) {
            throw response?.error || new Error("Failed to fetch crew users");
          }
          data = response.data;
        } else {
          const response = await axiosInstance.get(
            `${localStorage.getItem("projectSlug")}${path}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              },
              withCredentials: true,
            }
          );
          if (response?.success === false || response?.status === false) {
            throw response?.error || new Error("Failed to fetch crew users");
          }
          data = response.data;
        }

        const users = getUsersPayloadArray(data);
        setCrewOptions(users);

        // Cache the full list when we fetch without search, so later clearing search
        // can restore instantly without another API hit.
        if (!String(searchValue || "").trim()) {
          setCrewOptionsAll(users);
        }
      } catch (err) {
        console.log(err);
        showErrorToast(err, "Failed to load crew names.");
      } finally {
        setCrewSearchLoading(false);
      }
    },
    [host]
  );

  // Initial crew load (no search).
  useEffect(() => {
    if (host === CLIENT_PROGRAM_HOSTNAME) {
      fetchCrewOptions("");
    } else if (localStorage.getItem("accessToken")) {
      fetchCrewOptions("");
    }
  }, [fetchCrewOptions, host]);

  // Debounced server-side search (only sends `search` when user types).
  const didInitCrewSearch = useRef(false);
  useEffect(() => {
    // Skip the first render; initial load is handled above.
    if (!didInitCrewSearch.current) {
      didInitCrewSearch.current = true;
      return;
    }

    // If search is cleared, restore cached full list without hitting the API.
    if (!String(crewSearchQuery || "").trim()) {
      setCrewOptions(crewOptionsAll);
      return;
    }

    const timer = setTimeout(() => {
      fetchCrewOptions(crewSearchQuery);
    }, 350);

    return () => clearTimeout(timer);
  }, [crewSearchQuery, crewOptionsAll, fetchCrewOptions]);

  const handleChange = (event) => {
    if (CREW_READ_ONLY_FIELD_NAMES.has(event.target.name)) {
      return;
    }
    setPostData({
      ...postData,
      [event.target.name]: event.target.value,
    });
  };

  // Crew contact fields are autofilled from the selected user, so keep them
  // read-only instead of allowing manual edits that can desync the dropdown.
  useEffect(() => {
    CREW_READ_ONLY_FIELD_NAMES.forEach((fieldName) => {
      document
        .querySelectorAll(`input[name="${fieldName}"]`)
        .forEach((element) => {
          element.readOnly = true;
        });
      });
  });

  // When editing existing Crew Info, infer the selected dropdown value from
  // the saved name/email/phone so each role shows the matching crew member.
  useEffect(() => {
    if (!crewOptions.length) {
      return;
    }

    setSelectedCrewIds((prev) => {
      const next = { ...prev };
      let changed = false;

      CREW_ROLE_PREFIXES.forEach((rolePrefix) => {
        if (next[rolePrefix]) {
          return;
        }

        const savedName = normalizeValue(postData?.[`${rolePrefix}_name`]);
        const savedEmail = normalizeValue(postData?.[`${rolePrefix}_email`]);
        const savedPhone = normalizePhone(postData?.[`${rolePrefix}_phone`]);

        if (!savedName && !savedEmail && !savedPhone) {
          return;
        }

        const matchedUser = crewOptions.find((user) => {
          const userName = normalizeValue(getFullName(user));
          const userCallTimeUsername = normalizeValue(user?.call_time_username);
          const userUsername = normalizeValue(user?.username);
          const userEmail = normalizeValue(user?.email);
          const userPhone = normalizePhone(
            user?.phone_number || user?.mobile || ""
          );

          if (savedEmail && userEmail && savedEmail === userEmail) {
            return true;
          }

          if (savedPhone && userPhone && savedPhone === userPhone) {
            return true;
          }

          return (
            savedName &&
            ((userName && savedName === userName) ||
              (userCallTimeUsername && savedName === userCallTimeUsername) ||
              (userUsername && savedName === userUsername))
          );
        });

        if (matchedUser) {
          next[rolePrefix] = String(matchedUser.id);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [crewOptions, postData]);

  const handleCrewSelection = (rolePrefix) => (valueOrEvent) => {
    console.log(rolePrefix);
    const crewId =
      typeof valueOrEvent === "string" || valueOrEvent === null
        ? valueOrEvent || ""
        : valueOrEvent?.target?.value || "";
    setSelectedCrewIds((prev) => ({
      ...prev,
      [rolePrefix]: crewId,
    }));

    if (!crewId) {
      setPostData((prev) => ({
        ...prev,
        [`${rolePrefix}_name`]: "",
        [`${rolePrefix}_email`]: "",
        [`${rolePrefix}_phone`]: "",
      }));
      return;
    }

    const selectedUser = crewOptions.find(
      (user) => String(user?.id) === String(crewId)
    );
    if (!selectedUser) {
      return;
    }

    const fullName =
      getFullName(selectedUser) || "";

    setPostData((prev) => ({
      ...prev,
      [`${rolePrefix}_name`]: fullName,
      [`${rolePrefix}_email`]: selectedUser.email || "",
      [`${rolePrefix}_phone`]:
        selectedUser.phone_number || selectedUser.mobile || "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();

    // CREW MAPPING: send selected User.id into `${rolePrefix}_user` (Django FK fields).
    // Always append (empty string clears nullable FK on update for multipart FormData).
    CREW_ROLE_PREFIXES.forEach((rolePrefix) => {
      formData.append(
        `${rolePrefix}_user`,
        selectedCrewIds?.[rolePrefix] || ""
      );
    });

    formData.append("total_shoot_days", total_shoot_days);
    formData.append("production_name", production_name);
    formData.append("production_company", production_company);
    formData.append("company_phone", company_phone);
    formData.append("company_email", company_email);
    formData.append("company_address_line_1", company_address_line_1);
    formData.append("company_address_line_2", company_address_line_2);
    formData.append("company_address_line_3", company_address_line_3);
    formData.append("company_address_line_4", company_address_line_4);
    formData.append("director_name", director_name);
    formData.append("director_email", director_email);
    formData.append("director_phone", director_phone);
    formData.append("producer_name", producer_name);
    formData.append("producer_email", producer_email);
    formData.append("producer_phone", producer_phone);
    formData.append("pro_coordinator_name", pro_coordinator_name);
    formData.append("pro_coordinator_email", pro_coordinator_email);
    formData.append("pro_coordinator_phone", pro_coordinator_phone);
    formData.append("upm_name", upm_name);
    formData.append("upm_email", upm_email);
    formData.append("upm_phone", upm_phone);
    formData.append("travel_coordinator_name", travel_coordinator_name);
    formData.append("travel_coordinator_email", travel_coordinator_email);
    formData.append("travel_coordinator_phone", travel_coordinator_phone);
    formData.append("production_pa_name", production_pa_name);
    formData.append("production_pa_email", production_pa_email);
    formData.append("production_pa_phone", production_pa_phone);
    formData.append("oth_production_pos_1_job", oth_production_pos_1_job);
    formData.append("oth_production_pos_1_name", oth_production_pos_1_name);
    formData.append("oth_production_pos_1_email", oth_production_pos_1_email);
    formData.append("oth_production_pos_1_phone", oth_production_pos_1_phone);
    formData.append("oth_production_pos_2_job", oth_production_pos_2_job);
    formData.append("oth_production_pos_2_name", oth_production_pos_2_name);
    formData.append("oth_production_pos_2_email", oth_production_pos_2_email);
    formData.append("oth_production_pos_2_phone", oth_production_pos_2_phone);
    formData.append("oth_production_pos_3_job", oth_production_pos_3_job);
    formData.append("oth_production_pos_3_name", oth_production_pos_3_name);
    formData.append("oth_production_pos_3_email", oth_production_pos_3_email);
    formData.append("oth_production_pos_3_phone", oth_production_pos_3_phone);
    formData.append("oth_production_pos_4_job", oth_production_pos_4_job);
    formData.append("oth_production_pos_4_name", oth_production_pos_4_name);
    formData.append("oth_production_pos_4_email", oth_production_pos_4_email);
    formData.append("oth_production_pos_4_phone", oth_production_pos_4_phone);
    formData.append("oth_production_pos_5_job", oth_production_pos_5_job);
    formData.append("oth_production_pos_5_name", oth_production_pos_5_name);
    formData.append("oth_production_pos_5_email", oth_production_pos_5_email);
    formData.append("oth_production_pos_5_phone", oth_production_pos_5_phone);
    formData.append("ad_1_name", ad_1_name);
    formData.append("ad_1_email", ad_1_email);
    formData.append("ad_1_phone", ad_1_phone);
    formData.append("ad_2_name", ad_2_name);
    formData.append("ad_2_email", ad_2_email);
    formData.append("ad_2_phone", ad_2_phone);
    formData.append("ad_3_name", ad_3_name);
    formData.append("ad_3_email", ad_3_email);
    formData.append("ad_3_phone", ad_3_phone);
    formData.append("ad_4_name", ad_4_name);
    formData.append("ad_4_email", ad_4_email);
    formData.append("ad_4_phone", ad_4_phone);
    formData.append("ad_5_name", ad_5_name);
    formData.append("ad_5_email", ad_5_email);
    formData.append("ad_5_phone", ad_5_phone);
    formData.append("pro_assistant_1_name", pro_assistant_1_name);
    formData.append("pro_assistant_1_email", pro_assistant_1_email);
    formData.append("pro_assistant_1_phone", pro_assistant_1_phone);
    formData.append("pro_assistant_2_name", pro_assistant_2_name);
    formData.append("pro_assistant_2_email", pro_assistant_2_email);
    formData.append("pro_assistant_2_phone", pro_assistant_2_phone);
    formData.append("pro_assistant_3_name", pro_assistant_3_name);
    formData.append("pro_assistant_3_email", pro_assistant_3_email);
    formData.append("pro_assistant_3_phone", pro_assistant_3_phone);
    formData.append("pro_assistant_4_name", pro_assistant_4_name);
    formData.append("pro_assistant_4_email", pro_assistant_4_email);
    formData.append("pro_assistant_4_phone", pro_assistant_4_phone);
    formData.append("pro_assistant_5_name", pro_assistant_5_name);
    formData.append("pro_assistant_5_email", pro_assistant_5_email);
    formData.append("pro_assistant_5_phone", pro_assistant_5_phone);
    formData.append("dop_name", dop_name);
    formData.append("dop_email", dop_email);
    formData.append("dop_phone", dop_phone);
    formData.append("camera_operator_name", camera_operator_name);
    formData.append("camera_operator_email", camera_operator_email);
    formData.append("camera_operator_phone", camera_operator_phone);
    formData.append("camera_ass_1_name", camera_ass_1_name);
    formData.append("camera_ass_1_email", camera_ass_1_email);
    formData.append("camera_ass_1_phone", camera_ass_1_phone);
    formData.append("camera_ass_2_name", camera_ass_2_name);
    formData.append("camera_ass_2_email", camera_ass_2_email);
    formData.append("camera_ass_2_phone", camera_ass_2_phone);
    formData.append("dit_name", dit_name);
    formData.append("dit_email", dit_email);
    formData.append("dit_phone", dit_phone);
    formData.append("steadicam_name", steadicam_name);
    formData.append("steadicam_email", steadicam_email);
    formData.append("steadicam_phone", steadicam_phone);
    formData.append("camera_pa_name", camera_pa_name);
    formData.append("camera_pa_email", camera_pa_email);
    formData.append("camera_pa_phone", camera_pa_phone);
    formData.append("oth_camera_pos_1_job", oth_camera_pos_1_job);
    formData.append("oth_camera_pos_1_name", oth_camera_pos_1_name);
    formData.append("oth_camera_pos_1_email", oth_camera_pos_1_email);
    formData.append("oth_camera_pos_1_phone", oth_camera_pos_1_phone);
    formData.append("oth_camera_pos_2_job", oth_camera_pos_2_job);
    formData.append("oth_camera_pos_2_name", oth_camera_pos_2_name);
    formData.append("oth_camera_pos_2_email", oth_camera_pos_2_email);
    formData.append("oth_camera_pos_2_phone", oth_camera_pos_2_phone);
    formData.append("oth_camera_pos_3_job", oth_camera_pos_3_job);
    formData.append("oth_camera_pos_3_name", oth_camera_pos_3_name);
    formData.append("oth_camera_pos_3_email", oth_camera_pos_3_email);
    formData.append("oth_camera_pos_3_phone", oth_camera_pos_3_phone);
    formData.append("oth_camera_pos_4_job", oth_camera_pos_4_job);
    formData.append("oth_camera_pos_4_name", oth_camera_pos_4_name);
    formData.append("oth_camera_pos_4_email", oth_camera_pos_4_email);
    formData.append("oth_camera_pos_4_phone", oth_camera_pos_4_phone);
    formData.append("oth_camera_pos_5_job", oth_camera_pos_5_job);
    formData.append("oth_camera_pos_5_name", oth_camera_pos_5_name);
    formData.append("oth_camera_pos_5_email", oth_camera_pos_5_email);
    formData.append("oth_camera_pos_5_phone", oth_camera_pos_5_phone);
    // sound
    formData.append("sound_mixer_name", sound_mixer_name);
    formData.append("sound_mixer_email", sound_mixer_email);
    formData.append("sound_mixer_phone", sound_mixer_phone);
    formData.append("boom_operator_name", boom_operator_name);
    formData.append("boom_operator_email", boom_operator_email);
    formData.append("boom_operator_phone", boom_operator_phone);
    formData.append("sound_assistant_1_name", sound_assistant_1_name);
    formData.append("sound_assistant_1_email", sound_assistant_1_email);
    formData.append("sound_assistant_1_phone", sound_assistant_1_phone);
    formData.append("sound_assistant_2_name", sound_assistant_2_name);
    formData.append("sound_assistant_2_email", sound_assistant_2_email);
    formData.append("sound_assistant_2_phone", sound_assistant_2_phone);
    formData.append("script_supervisor_name", script_supervisor_name);
    formData.append("script_supervisor_email", script_supervisor_email);
    formData.append("script_supervisor_phone", script_supervisor_phone);
    formData.append("key_hairmakeup_name", key_hairmakeup_name);
    formData.append("key_hairmakeup_email", key_hairmakeup_email);
    formData.append("key_hairmakeup_phone", key_hairmakeup_phone);
    formData.append("key_hairstylist_name", key_hairstylist_name);
    formData.append("key_hairstylist_email", key_hairstylist_email);
    formData.append("key_hairstylist_phone", key_hairstylist_phone);
    formData.append("sfx_makeup_name", sfx_makeup_name);
    formData.append("sfx_makeup_email", sfx_makeup_email);
    formData.append("sfx_makeup_phone", sfx_makeup_phone);
    formData.append("sfx_makeup_assistant_name", sfx_makeup_assistant_name);
    formData.append("sfx_makeup_assistant_email", sfx_makeup_assistant_email);
    formData.append("sfx_makeup_assistant_phone", sfx_makeup_assistant_phone);
    formData.append("makeup_artist_1_name", makeup_artist_1_name);
    formData.append("makeup_artist_1_email", makeup_artist_1_email);
    formData.append("makeup_artist_1_phone", makeup_artist_1_phone);
    formData.append("makeup_artist_2_name", makeup_artist_2_name);
    formData.append("makeup_artist_2_email", makeup_artist_2_email);
    formData.append("makeup_artist_2_phone", makeup_artist_2_phone);
    formData.append("makeup_artist_3_name", makeup_artist_3_name);
    formData.append("makeup_artist_3_email", makeup_artist_3_email);
    formData.append("makeup_artist_3_phone", makeup_artist_3_phone);
    formData.append("makeup_artist_4_name", makeup_artist_4_name);
    formData.append("makeup_artist_4_email", makeup_artist_4_email);
    formData.append("makeup_artist_4_phone", makeup_artist_4_phone);
    formData.append("makeup_artist_5_name", makeup_artist_5_name);
    formData.append("makeup_artist_5_email", makeup_artist_5_email);
    formData.append("makeup_artist_5_phone", makeup_artist_5_phone);
    formData.append("costume_designer_name", costume_designer_name);
    formData.append("costume_designer_email", costume_designer_email);
    formData.append("costume_designer_phone", costume_designer_phone);
    formData.append("ass_costume_designer_name", ass_costume_designer_name);
    formData.append("ass_costume_designer_email", ass_costume_designer_email);
    formData.append("ass_costume_designer_phone", ass_costume_designer_phone);
    formData.append("wardrobe_assistant_1_name", wardrobe_assistant_1_name);
    formData.append("wardrobe_assistant_1_email", wardrobe_assistant_1_email);
    formData.append("wardrobe_assistant_1_phone", wardrobe_assistant_1_phone);
    formData.append("wardrobe_assistant_2_name", wardrobe_assistant_2_name);
    formData.append("wardrobe_assistant_2_email", wardrobe_assistant_2_email);
    formData.append("wardrobe_assistant_2_phone", wardrobe_assistant_2_phone);
    formData.append("wardrobe_assistant_3_name", wardrobe_assistant_3_name);
    formData.append("wardrobe_assistant_3_email", wardrobe_assistant_3_email);
    formData.append("wardrobe_assistant_3_phone", wardrobe_assistant_3_phone);
    formData.append("wardrobe_assistant_4_name", wardrobe_assistant_4_name);
    formData.append("wardrobe_assistant_4_email", wardrobe_assistant_4_email);
    formData.append("wardrobe_assistant_4_phone", wardrobe_assistant_4_phone);
    formData.append("wardrobe_assistant_5_name", wardrobe_assistant_5_name);
    formData.append("wardrobe_assistant_5_email", wardrobe_assistant_5_email);
    formData.append("wardrobe_assistant_5_phone", wardrobe_assistant_5_phone);
    formData.append("production_designer_name", production_designer_name);
    formData.append("production_designer_email", production_designer_email);
    formData.append("production_designer_phone", production_designer_phone);
    formData.append("art_director_name", art_director_name);
    formData.append("art_director_email", art_director_email);
    formData.append("art_director_phone", art_director_phone);
    formData.append("art_assistant_name", art_assistant_name);
    formData.append("art_assistant_email", art_assistant_email);
    formData.append("art_assistant_phone", art_assistant_phone);
    formData.append("set_decorator_name", set_decorator_name);
    formData.append("set_decorator_email", set_decorator_email);
    formData.append("set_decorator_phone", set_decorator_phone);
    formData.append("set_dresser_name", set_dresser_name);
    formData.append("set_dresser_email", set_dresser_email);
    formData.append("set_dresser_phone", set_dresser_phone);
    formData.append("lead_man_name", lead_man_name);
    formData.append("lead_man_email", lead_man_email);
    formData.append("lead_man_phone", lead_man_phone);
    formData.append("dresser_name", dresser_name);
    formData.append("dresser_email", dresser_email);
    formData.append("dresser_phone", dresser_phone);
    formData.append("prop_master_name", prop_master_name);
    formData.append("prop_master_email", prop_master_email);
    formData.append("prop_master_phone", prop_master_phone);
    formData.append("ass_prop_master_name", ass_prop_master_name);
    formData.append("ass_prop_master_email", ass_prop_master_email);
    formData.append("ass_prop_master_phone", ass_prop_master_phone);
    formData.append("prop_buyer_name", prop_buyer_name);
    formData.append("prop_buyer_email", prop_buyer_email);
    formData.append("prop_buyer_phone", prop_buyer_phone);
    formData.append("armorer_name", armorer_name);
    formData.append("armorer_email", armorer_email);
    formData.append("armorer_phone", armorer_phone);
    formData.append("gaffer_name", gaffer_name);
    formData.append("gaffer_email", gaffer_email);
    formData.append("gaffer_phone", gaffer_phone);
    formData.append("best_boy_electric_name", best_boy_electric_name);
    formData.append("best_boy_electric_email", best_boy_electric_email);
    formData.append("best_boy_electric_phone", best_boy_electric_phone);
    formData.append("electric_3_name", electric_3_name);
    formData.append("electric_3_phone", electric_3_phone);
    formData.append("electric_3_email", electric_3_email);
    formData.append("electric_4_name", electric_4_name);
    formData.append("electric_4_email", electric_4_email);
    formData.append("electric_4_phone", electric_4_phone);
    formData.append("keygrip_name", keygrip_name);
    formData.append("keygrip_email", keygrip_email);
    formData.append("keygrip_phone", keygrip_phone);
    formData.append("best_boy_grip_name", best_boy_grip_name);
    formData.append("best_boy_grip_email", best_boy_grip_email);
    formData.append("best_boy_grip_phone", best_boy_grip_phone);
    formData.append("dolly_grip_name", dolly_grip_name);
    formData.append("dolly_grip_email", dolly_grip_email);
    formData.append("dolly_grip_phone", dolly_grip_phone);
    formData.append("swing_ge1_name", swing_ge1_name);
    formData.append("swing_ge1_email", swing_ge1_email);
    formData.append("swing_ge1_phone", swing_ge1_phone);
    formData.append("swing_ge2_name", swing_ge2_name);
    formData.append("swing_ge2_email", swing_ge2_email);
    formData.append("swing_ge2_phone", swing_ge2_phone);
    formData.append("swing_ge3_name", swing_ge3_name);
    formData.append("swing_ge3_email", swing_ge3_email);
    formData.append("swing_ge3_phone", swing_ge3_phone);
    formData.append("swing_ge4_name", swing_ge4_name);
    formData.append("swing_ge4_email", swing_ge4_email);
    formData.append("swing_ge4_phone", swing_ge4_phone);
    formData.append("swing_ge5_name", swing_ge5_name);
    formData.append("swing_ge5_email", swing_ge5_email);
    formData.append("swing_ge5_phone", swing_ge5_phone);
    formData.append("stunt_coordinator_name", stunt_coordinator_name);
    formData.append("stunt_coordinator_email", stunt_coordinator_email);
    formData.append("stunt_coordinator_phone", stunt_coordinator_phone);
    formData.append("stunts_1_name", stunts_1_name);
    formData.append("stunts_1_email", stunts_1_email);
    formData.append("stunts_1_phone", stunts_1_phone);
    formData.append("stunts_2_name", stunts_2_name);
    formData.append("stunts_2_email", stunts_2_email);
    formData.append("stunts_2_phone", stunts_2_phone);
    formData.append("stunts_3_name", stunts_3_name);
    formData.append("stunts_3_email", stunts_3_email);
    formData.append("stunts_3_phone", stunts_3_phone);
    formData.append("stunts_4_name", stunts_4_name);
    formData.append("stunts_4_email", stunts_4_email);
    formData.append("stunts_4_phone", stunts_4_phone);
    formData.append("stunts_5_name", stunts_5_name);
    formData.append("stunts_5_email", stunts_5_email);
    formData.append("stunts_5_phone", stunts_5_phone);
    formData.append("legal_name", legal_name);
    formData.append("legal_email", legal_email);
    formData.append("legal_phone", legal_phone);
    formData.append("editor_name", editor_name);
    formData.append("editor_email", editor_email);
    formData.append("editor_phone", editor_phone);
    formData.append("writer_name", writer_name);
    formData.append("writer_email", writer_email);
    formData.append("writer_phone", writer_phone);
    formData.append("fx_name", fx_name);
    formData.append("fx_email", fx_email);
    formData.append("fx_phone", fx_phone);
    formData.append("set_medic_name", set_medic_name);
    formData.append("set_medic_email", set_medic_email);
    formData.append("set_medic_phone", set_medic_phone);
    formData.append("casting_director_name", casting_director_name);
    formData.append("casting_director_email", casting_director_email);
    formData.append("casting_director_phone", casting_director_phone);
    formData.append("extras_casting_name", extras_casting_name);
    formData.append("extras_casting_email", extras_casting_email);
    formData.append("extras_casting_phone", extras_casting_phone);
    formData.append("location_mngr_name", location_mngr_name);
    formData.append("location_mngr_email", location_mngr_email);
    formData.append("location_mngr_phone", location_mngr_phone);
    formData.append("location_ass_1_name", location_ass_1_name);
    formData.append("location_ass_1_email", location_ass_1_email);
    formData.append("location_ass_1_phone", location_ass_1_phone);
    formData.append("location_ass_2_name", location_ass_2_name);
    formData.append("location_ass_2_email", location_ass_2_email);
    formData.append("location_ass_2_phone", location_ass_2_phone);
    formData.append("location_ass_3_name", location_ass_3_name);
    formData.append("location_ass_3_email", location_ass_3_email);
    formData.append("location_ass_3_phone", location_ass_3_phone);
    formData.append("location_ass_4_name", location_ass_4_name);
    formData.append("location_ass_4_email", location_ass_4_email);
    formData.append("location_ass_4_phone", location_ass_4_phone);
    formData.append("location_security_name", location_security_name);
    formData.append("location_security_email", location_security_email);
    formData.append("location_security_phone", location_security_phone);
    formData.append("transport_captain_name", transport_captain_name);
    formData.append("transport_captain_email", transport_captain_email);
    formData.append("transport_captain_phone", transport_captain_phone);
    formData.append("transport_manager_1_name", transport_manager_1_name);
    formData.append("transport_manager_1_email", transport_manager_1_email);
    formData.append("transport_manager_1_phone", transport_manager_1_phone);
    formData.append("transport_manager_2_name", transport_manager_2_name);
    formData.append("transport_manager_2_email", transport_manager_2_email);
    formData.append("transport_manager_2_phone", transport_manager_2_phone);
    formData.append("head_driver_name", head_driver_name);
    formData.append("head_driver_email", head_driver_email);
    formData.append("head_driver_phone", head_driver_phone);
    formData.append("truck1_name", truck1_name);
    formData.append("truck1_email", truck1_email);
    formData.append("truck1_phone", truck1_phone);
    formData.append("truck2_name", truck2_name);
    formData.append("truck2_email", truck2_email);
    formData.append("truck2_phone", truck2_phone);
    formData.append("truck3_name", truck3_name);
    formData.append("truck3_email", truck3_email);
    formData.append("truck3_phone", truck3_phone);
    formData.append("car1_name", car1_name);
    formData.append("car1_email", car1_email);
    formData.append("car1_phone", car1_phone);
    formData.append("car2_name", car2_name);
    formData.append("car2_email", car2_email);
    formData.append("car2_phone", car2_phone);
    formData.append("car3_name", car3_name);
    formData.append("car3_email", car3_email);
    formData.append("car3_phone", car3_phone);
    formData.append("catering_co_1_name", catering_co_1_name);
    formData.append("catering_co_1_email", catering_co_1_email);
    formData.append("catering_co_1_phone", catering_co_1_phone);
    formData.append("catering_co_2_name", catering_co_2_name);
    formData.append("catering_co_2_email", catering_co_2_email);
    formData.append("catering_co_2_phone", catering_co_2_phone);
    formData.append("catering_co_3_name", catering_co_3_name);
    formData.append("catering_co_3_email", catering_co_3_email);
    formData.append("catering_co_3_phone", catering_co_3_phone);
    formData.append("craft_service_name", craft_service_name);
    formData.append("craft_service_email", craft_service_email);
    formData.append("craft_service_phone", craft_service_phone);
    formData.append("crafty_ass_name", crafty_ass_name);
    formData.append("crafty_ass_email", crafty_ass_email);
    formData.append("crafty_ass_phone", crafty_ass_phone);
    formData.append("add_pos_1_job", add_pos_1_job);
    formData.append("add_pos_1_name", add_pos_1_name);
    formData.append("add_pos_1_email", add_pos_1_email);
    formData.append("add_pos_1_phone", add_pos_1_phone);
    formData.append("add_pos_2_job", add_pos_2_job);
    formData.append("add_pos_2_name", add_pos_2_name);
    formData.append("add_pos_2_email", add_pos_2_email);
    formData.append("add_pos_2_phone", add_pos_2_phone);
    formData.append("add_pos_3_job", add_pos_3_job);
    formData.append("add_pos_3_name", add_pos_3_name);
    formData.append("add_pos_3_email", add_pos_3_email);
    formData.append("add_pos_3_phone", add_pos_3_phone);
    formData.append("add_pos_4_job", add_pos_4_job);
    formData.append("add_pos_4_name", add_pos_4_name);
    formData.append("add_pos_4_email", add_pos_4_email);
    formData.append("add_pos_4_phone", add_pos_4_phone);
    formData.append("add_pos_5_job", add_pos_5_job);
    formData.append("add_pos_5_name", add_pos_5_name);
    formData.append("add_pos_5_email", add_pos_5_email);
    formData.append("add_pos_5_phone", add_pos_5_phone);
    formData.append("add_pos_6_job", add_pos_6_job);
    formData.append("add_pos_6_name", add_pos_6_name);
    formData.append("add_pos_6_email", add_pos_6_email);
    formData.append("add_pos_6_phone", add_pos_6_phone);
    formData.append("add_pos_7_job", add_pos_7_job);
    formData.append("add_pos_7_name", add_pos_7_name);
    formData.append("add_pos_7_email", add_pos_7_email);
    formData.append("add_pos_7_phone", add_pos_7_phone);
    formData.append("add_pos_8_job", add_pos_8_job);
    formData.append("add_pos_8_name", add_pos_8_name);
    formData.append("add_pos_8_email", add_pos_8_email);
    formData.append("add_pos_8_phone", add_pos_8_phone);
    formData.append("add_pos_9_job", add_pos_9_job);
    formData.append("add_pos_9_name", add_pos_9_name);
    formData.append("add_pos_9_email", add_pos_9_email);
    formData.append("add_pos_9_phone", add_pos_9_phone);
    formData.append("add_pos_10_job", add_pos_10_job);
    formData.append("add_pos_10_name", add_pos_10_name);
    formData.append("add_pos_10_email", add_pos_10_email);
    formData.append("add_pos_10_phone", add_pos_10_phone);
    // addttional positions 11-20
    formData.append("add_pos_11_job", add_pos_11_job);
    formData.append("add_pos_11_name", add_pos_11_name);
    formData.append("add_pos_11_email", add_pos_11_email);
    formData.append("add_pos_11_phone", add_pos_11_phone);
    formData.append("add_pos_12_job", add_pos_12_job);
    formData.append("add_pos_12_name", add_pos_12_name);
    formData.append("add_pos_12_email", add_pos_12_email);
    formData.append("add_pos_12_phone", add_pos_12_phone);
    formData.append("add_pos_13_job", add_pos_13_job);
    formData.append("add_pos_13_name", add_pos_13_name);
    formData.append("add_pos_13_email", add_pos_13_email);
    formData.append("add_pos_13_phone", add_pos_13_phone);
    formData.append("add_pos_14_job", add_pos_14_job);
    formData.append("add_pos_14_name", add_pos_14_name);
    formData.append("add_pos_14_email", add_pos_14_email);
    formData.append("add_pos_14_phone", add_pos_14_phone);
    formData.append("add_pos_15_job", add_pos_15_job);
    formData.append("add_pos_15_name", add_pos_15_name);
    formData.append("add_pos_15_email", add_pos_15_email);
    formData.append("add_pos_15_phone", add_pos_15_phone);
    formData.append("add_pos_16_job", add_pos_16_job);
    formData.append("add_pos_16_name", add_pos_16_name);
    formData.append("add_pos_16_email", add_pos_16_email);
    formData.append("add_pos_16_phone", add_pos_16_phone);
    formData.append("add_pos_17_job", add_pos_17_job);
    formData.append("add_pos_17_name", add_pos_17_name);
    formData.append("add_pos_17_email", add_pos_17_email);
    formData.append("add_pos_17_phone", add_pos_17_phone);
    formData.append("add_pos_18_job", add_pos_18_job);
    formData.append("add_pos_18_name", add_pos_18_name);
    formData.append("add_pos_18_email", add_pos_18_email);
    formData.append("add_pos_18_phone", add_pos_18_phone);
    formData.append("add_pos_19_job", add_pos_19_job);
    formData.append("add_pos_19_name", add_pos_19_name);
    formData.append("add_pos_19_email", add_pos_19_email);
    formData.append("add_pos_19_phone", add_pos_19_phone);
    formData.append("add_pos_20_job", add_pos_20_job);
    formData.append("add_pos_20_name", add_pos_20_name);
    formData.append("add_pos_20_email", add_pos_20_email);
    formData.append("add_pos_20_phone", add_pos_20_phone);
    try {
      if (host === CLIENT_PROGRAM_HOSTNAME) {
        const response = await axiosReq.put(
          `/crewinfonew/${crewInfoData?.id}/`,
          formData
        );
        if (response?.success === false || response?.status === false) {
          throw response?.error || new Error("Failed to update crew info");
        }
        const { data } = response;
        setEditCrewInfo(true);
        history.goBack();
        toast.success(`Crew Info Updated`);
      } else {
        const response = await axiosInstance.put(
          `/${localStorage.getItem("projectSlug")}/crewinfonew/${
            crewInfoData?.id
          }/`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            withCredentials: true,
          }
        );
        if (response?.success === false || response?.status === false) {
          throw response?.error || new Error("Failed to update crew info");
        }
        const { data } = response;
        setEditCrewInfo(true);
        console.log(data);
        history.goBack();
        toast.success(`Crew Info Updated Successfully!`);
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to update crew info");
      if (err.response?.status !== 401) {
        setErrors(err.response?.data);
      }
    }
  };

  const buttons = (
    <div className="text-center pb-3 mb-4 mt-4">
      <Button
        className={`${btnStyles.Button} ${btnStyles.Blue} px-3 px-md-5 mr-3`}
        onClick={() => history.goBack()}
      >
        Cancel
      </Button>
      <Button className={`${btnStyles.Button} ${btnStyles.Blue} px-3 px-md-5 ml-3`} type="submit">
        Create
      </Button>
    </div>
  );

  return (
    <div>
      <TopBox work="Crew Info" title="Add / Edit" />
      <Button
        className={`${btnStyles.Button} ${btnStyles.Blue} py-0 mt-1`}
        onClick={() => history.goBack()}
      >
        Back
      </Button>
      <Button
        className={`float-right py-0 mt-1 ${btnStyles.Order} ${btnStyles.Button}`}
        onClick={() => setShowImp((showImp) => !showImp)}
      >
        INFO
      </Button>
      <h5 className={`text-center mt-2 py-1 ${styles.SubTitle }`}>
             ADD / EDIT CREW INFO</h5>
      {!showImp ? "" : <ImpoEditpage />}
      <Form className={`mb-3 ${styles.Back3}`} onSubmit={handleSubmit}>
      <div className={`pt-2 ${styles.White }`}> 
      <Row className={`${styles.ButtonLine} mt-0`}>
      <Col className='text-center'>
              <p
                className={`py-0 mb-0 ${styles.Button}`}
                onClick={() => setShowCom(showCom => !showCom)} > Company/Production
              </p>
          </Col>
      </Row>
      <Row className={`${styles.ButtonLine} mt-0`}>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
            <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowPro(showPro => !showPro)} > Production
            </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
                <p
                  className={`py-0 mb-0 ${styles.Button}`}
                  onClick={() => setShowCam(showCam => !showCam)} > Camera
                </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowAD(showAD => !showAD)} > AD/PA
          </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
        <p
            className={`py-0 mb-0 ${styles.Button}`}
            onClick={() => setShowLoc(showLoc => !showLoc)} >Locations
        </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowWar(showWar => !showWar)} >Wardrobe
          </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowScr(showScr => !showScr)} > Script
          </p>
        </Col>
      </Row>  
      <Row className={`${styles.ButtonLine} text-center mt-1`}>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
            <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowEle(showEle => !showEle)} > Electric/Grip
            </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
                <p
                  className={`py-0 mb-0 ${styles.Button}`}
                  onClick={() => setShowMak(showMak => !showMak)} > Makeup
                </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowSou(showSou => !showSou)} > Sound
          </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
        <p
            className={`py-0 mb-0 ${styles.Button}`}
            onClick={() => setShowStu(showStu => !showStu)} >Stunts/SFX
        </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowArt(showArt => !showArt)} > Art
          </p>
        </Col>
        <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowPos(showPos => !showPos)} > Post/VFX
          </p>
        </Col>
      </Row>  
      {/* new row - Catering - Casting - Transport - Wrangling - TV Studio -Additional */}
      <Row className={`${styles.ButtonLine} mt-1`}>
      <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowCat(showCat => !showCat)} > Catering/Craft
          </p>
      </Col>
      <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowCas(showCas => !showCas)} > Casting
          </p>
      </Col>
      <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowTra(showTra => !showTra)} > Transport
          </p>
      </Col>
      <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
              <p
                  className={`d-block d-lg-none py-0 mb-0 ${styles.Button}`}
                  onClick={() => setShowWra(showWra => !showWra)} > Construction/Wr
              </p>
              <p
                  className={`d-none d-lg-block py-0 mb-0 ${styles.Button}`}
                  onClick={() => setShowWra(showWra => !showWra)} > Construction/Wrangling
              </p>
          </Col>
      <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowTv(showTv => !showTv)} > TV Studio
          </p>
      </Col>
      <Col xs={4} md={2} className='mx-0 px-0 pt-1 text-center'>
          <p
              className={`py-0 mb-0 ${styles.Button}`}
              onClick={() => setShowAdd(showAdd => !showAdd)} >Additional
          </p>
      </Col>
      </Row>
      <p className={`mt-1 pl-3 mb-0 pt-1 ${styles.SubTitle }`}></p>
      <div className={`py-1 ${styles.White }`}>
      </div>
      </div>
      <div className={`mt-0`}>
      {/* Add Company */}
      {!showCom ? (
      ""
          ) : (
            <CrewCompany handleChange={handleChange}
            postData={postData} setShow={setShowCom} /> 
            ) }  
        {/* Add Production */}
        {!showPro ? (
          ""
              ) : (
                <CrewProduction handleChange={handleChange} 
                postData={postData}
                setShow={setShowPro}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) }  
        {/* Add Casting */}
        {!showCas ? (
                ""
              ) : (
                <CrewCasting setShow={setShowCas} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add AD/PA */}
        {!showAD ? (
                ""
              ) : (
                <CrewADPA setShow={setShowAD} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Script  */}
        {!showScr ? (
                ""
              ) : (
                <CrewScript setShow={setShowScr} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Catering  */}
        {!showCat ? (
                ""
              ) : (
                <CrewCater setShow={setShowCat} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) }
        {/* Add Sound */}
        {!showSou ? (
                ""
              ) : (
                <CrewSound setShow={setShowSou} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                )}
        {/* Add Transport */}
        {!showTra ? (
                ""
              ) : (
                <CrewTransport setShow={setShowTra} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Post */}
        {!showPos ? (
                ""
              ) : (
                <CrewPostVFX setShow={setShowPos} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Wrangling */}
        {!showWra ? (
                ""
              ) : (
                <CrewWrangling setShow={setShowWra} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add TV */}
        {!showTv ? (
                ""
              ) : (
                <CrewTVStudio setShow={setShowTv} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) }    
        {/* Add Additional */}
        {!showAdd ? (
                ""
              ) : (
                <CrewAdditional setShow={setShowAdd} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) }  
        {/* Add Locations */}
        {!showLoc ? (
                ""
              ) : (
                <CrewLocations setShow={setShowLoc} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) }  
        {/* Add Electric */}
        {!showEle ? (
                ""
              ) : (
                <CrewElecGrip setShow={setShowEle} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Makeup */}
        {!showMak ? (
                ""
              ) : (
                <CrewMakeup setShow={setShowMak} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Stunts */}
        {!showStu ? (
                ""
              ) : (
                <CrewStunts setShow={setShowStu} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Art */}
        {!showArt ? (
                ""
              ) : (
                <CrewArt setShow={setShowArt} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Camera */}
        {!showCam ? (
                ""
              ) : (
                <CrewCamera setShow={setShowCam} 
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
        {/* Add Wardrobe  */}
        {!showWar ? (
                ""
              ) : (
                <CrewWardrobe setShow={setShowWar}
                postData={postData}
                handleChange={handleChange}
                crewOptions={crewOptions}
                selectedCrewIds={selectedCrewIds}
                handleCrewSelection={handleCrewSelection}
                crewSearchQuery={crewSearchQuery}
                setCrewSearchQuery={setCrewSearchQuery}
                crewSearchLoading={crewSearchLoading}
                /> 
                ) } 
      </div>
        <div className={`py-2 ${styles.White}`}></div>
        {/* buttons */}
        <h3 className={` mb-0 py-1 ${styles.SubTitle}`}> </h3>
        <div className={`py-2 ${styles.White}`}></div>
        {buttons}
      </Form>
    </div>
  );
};

export default CrewInfoEdit;
